(function(){
  'use strict';

  // Reusable button-loading helper — works via the inline `event`
  // global available inside any function invoked from an onclick
  // attribute, so it can be dropped into existing save/action
  // functions without changing any HTML. Call at the top of a function
  // with `const restore=_withBtnSpin(event,'Saving...');`, then call
  // `restore()` before every return path (success or failure).
  function _withBtnSpin(evtOrBtn, label){
    const btn = evtOrBtn?.target?.closest ? evtOrBtn.target.closest('button') : evtOrBtn;
    if(!btn) return ()=>{};
    if(btn.dataset.spinning==='1') return ()=>{}; // already spinning, avoid double-wrap
    btn.dataset.spinning='1';
    const original = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = `<i class="ti ti-loader-2 ims__spin"></i> ${label||'Working...'}`;
    return () => { btn.disabled = false; btn.innerHTML = original; delete btn.dataset.spinning; };
  }
  const db=()=>window.SM.DB, ui=()=>window.SM.UI, util=()=>window.SM.UTIL;

  let _activeTab='customers';

  function initTabs(){
    const tabs=document.getElementById('ims-ds-tabs');
    if(!tabs)return;
    tabs.querySelectorAll('.ims__tab').forEach(tab=>{
      tab.addEventListener('click',()=>{
        tabs.querySelectorAll('.ims__tab').forEach(t=>t.classList.remove('ims__tab--active'));
        tab.classList.add('ims__tab--active');
        _activeTab=tab.dataset.tab;
        loadTab(_activeTab);
      });
    });
  }

  function loadTab(tab){
    switch(tab){
      case 'customers':  loadCustomers();  break;
      case 'orders':     loadOrders();     break;
      case 'allocations':loadAllocations();break;
      case 'invoices':   loadInvoices();   break;
      case 'reports':    loadReports();    break;
      case 'deals':      loadDSDeals();    break;
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // CUSTOMERS
  // ═══════════════════════════════════════════════════════════════
  async function loadCustomers(){
    const content=document.getElementById('ims-ds-content');
    if(!content)return;
    content.innerHTML=`<div class="ims__loading"><i class="ti ti-loader-2 ims__spin"></i></div>`;

    const actions=document.getElementById('ims-ds-actions');
    if(actions) actions.innerHTML=`<button class="ims__btn ims__btn--primary" onclick="window.IMS_DROPSHIP.showCustomerForm()"><i class="ti ti-plus"></i> Add dropship customer</button>`;

    const {data:customers}=await db().select('dropship_customers',{order:{col:'business_name'}});
    const items=customers||[];

    if(!items.length){
      content.innerHTML=`<div class="ims__coming-soon"><i class="ti ti-users"></i><h3>No dropship customers yet</h3><p>Add your first dropship customer to get started.</p></div>`;
      return;
    }

    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const {data:orders}=await client.from('orders').select('dropship_customer_id,status,delivery_method').eq('fulfillment_type','dropship');
    const {data:allocs}=await db().select('dropship_allocations',{select:'customer_id,remaining_qty'});

    const orderMap={}, allocMap={};
    (orders||[]).forEach(o=>{
      const cid=o.dropship_customer_id;
      if(!cid)return;
      if(!orderMap[cid])orderMap[cid]={total:0,active:0};
      orderMap[cid].total++;
      if(!['delivered','collected','cancelled'].includes(o.status))orderMap[cid].active++;
    });
    (allocs||[]).forEach(a=>{
      allocMap[a.customer_id]=(allocMap[a.customer_id]||0)+(a.remaining_qty||0);
    });

    content.innerHTML=`
      <div class="ims__table-wrap">
        <table class="ims__table">
          <thead><tr>
            <th>Business name</th><th>Contact</th><th>Delivery method</th>
            <th>Discount</th><th>Credit limit</th><th>Stock allocated</th><th>Active orders</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>${items.map(c=>`<tr>
            <td><span class="ims__link" onclick="window.IMS_DROPSHIP.openCustomer('${c.id}')"><strong>${util().sanitize(c.business_name)}</strong></span></td>
            <td style="font-size:12px">${util().sanitize(c.contact_name||'—')}<br><span style="color:var(--ims-text-muted)">${util().sanitize(c.email||'—')}</span></td>
            <td><span class="ims__pill ims__pill--${c.delivery_method==='mdf_delivers'?'info':'neutral'}">${c.delivery_method==='mdf_delivers'?'We deliver':'Self-arrange'}</span></td>
            <td style="font-weight:600;color:var(--ims-success)">${c.discount_pct||0}% off wholesale</td>
            <td style="font-weight:600">${c.credit_limit>0?util().formatCurrency(c.credit_limit):'—'}</td>
            <td style="font-weight:600">${allocMap[c.id]||0} units</td>
            <td>${orderMap[c.id]?.active||0} orders</td>
            <td><span class="ims__pill ims__pill--${c.is_active?'success':'neutral'}">${c.is_active?'Active':'Inactive'}</span></td>
            <td><div class="ims__btn-group">
              <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_DROPSHIP.openCustomer('${c.id}')" title="View"><i class="ti ti-eye"></i></button>
              <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_DROPSHIP.showCustomerForm('${c.id}')" title="Edit"><i class="ti ti-edit"></i></button>
              <button class="ims__btn ims__btn--primary ims__btn--sm" onclick="window.IMS_DROPSHIP.showAllocateForm('${c.id}','${util().sanitize(c.business_name)}')"><i class="ti ti-box"></i> Allocate stock</button>
              <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_DROPSHIP.showCustomerPricing('${c.id}','${util().sanitize(c.business_name)}')"><i class="ti ti-tag"></i> Custom pricing</button>
              ${window.SM.AUTH.isAdmin()?`<button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon" onclick="window.IMS_DROPSHIP.deleteCustomer('${c.id}','${util().sanitize(c.business_name)}')" title="Delete customer"><i class="ti ti-trash"></i></button>`:''}
            </div></td>
          </tr>`).join('')}
          </tbody>
        </table>
      </div>`;
  }

  async function openCustomer(id){
    const {data:c}=await db().select('dropship_customers',{eq:{id},maybeSingle:true,noCompanyFilter:true});
    if(!c)return;
    const {data:allocs}=await db().select('dropship_allocations',{
      select:'allocated_qty,remaining_qty,allocation_date,products(name,sku)',
      eq:{customer_id:id},noCompanyFilter:true
    });
    const {data:orders}=await db().select('orders',{
      select:'order_number,end_customer_name,end_postcode,status,total_value,created_at,delivery_method',
      eq:{dropship_customer_id:id,fulfillment_type:'dropship'},order:{col:'order_number',asc:false},limit:10,noCompanyFilter:true
    });
    const totalAllocated=(allocs||[]).reduce((s,a)=>s+(a.allocated_qty||0),0);
    const totalRemaining=(allocs||[]).reduce((s,a)=>s+(a.remaining_qty||0),0);
    const totalSold=totalAllocated-totalRemaining;

    ui().openModal(`Dropship: ${c.business_name}`,`
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px">
        <div><span class="ims__label">Business name</span><div style="font-weight:600">${util().sanitize(c.business_name)}</div></div>
        <div><span class="ims__label">Contact</span><div>${util().sanitize(c.contact_name||'—')}</div></div>
        <div><span class="ims__label">Email</span><div>${util().sanitize(c.email||'—')}</div></div>
        <div><span class="ims__label">Phone</span><div>${util().sanitize(c.phone||'—')}</div></div>
        <div><span class="ims__label">Delivery method</span><div><span class="ims__pill ims__pill--${c.delivery_method==='mdf_delivers'?'info':'neutral'}">${c.delivery_method==='mdf_delivers'?'MDF London delivers':'Customer self-arranges'}</span></div></div>
        <div><span class="ims__label">Discount</span><div style="font-size:18px;font-weight:700;color:var(--ims-success)">${c.discount_pct||0}% off wholesale</div></div>
        <div><span class="ims__label">Invoice cycle</span><div>Fortnightly</div></div>
        <div><span class="ims__label">Credit terms</span><div>${util().sanitize(c.credit_terms||'Fortnightly')}</div></div>
        <div><span class="ims__label">Credit limit</span><div style="font-weight:600;color:${c.credit_limit>0?'var(--ims-text-primary)':'var(--ims-text-muted)'}">${c.credit_limit>0?util().formatCurrency(c.credit_limit):'No limit set'}</div></div>
        ${c.notes?`<div style="grid-column:1/-1"><span class="ims__label">Notes</span><div>${util().sanitize(c.notes)}</div></div>`:''}
      </div>
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:14px">
        <div class="ims__stat" style="padding:10px"><div class="ims__stat-label">Total allocated</div><div class="ims__stat-value" style="font-size:20px">${totalAllocated}</div></div>
        <div class="ims__stat" style="padding:10px"><div class="ims__stat-label">Sold / delivered</div><div class="ims__stat-value" style="font-size:20px">${totalSold}</div></div>
        <div class="ims__stat" style="padding:10px"><div class="ims__stat-label">Remaining</div><div class="ims__stat-value ims__stat-value--${totalRemaining>0?'success':'warning'}" style="font-size:20px">${totalRemaining}</div></div>
      </div>
      <hr class="ims__divider">
      <strong style="font-size:13px">Stock allocations</strong>
      <table class="ims__table" style="margin-top:8px">
        <thead><tr><th>Product</th><th>SKU</th><th>Allocated</th><th>Remaining</th><th>Date</th></tr></thead>
        <tbody>${(allocs||[]).map(a=>`<tr>
          <td>${util().sanitize(a.products?.name||'—')}</td>
          <td>${util().sanitize(a.products?.sku||'—')}</td>
          <td>${a.allocated_qty||0}</td>
          <td style="font-weight:600;color:${(a.remaining_qty||0)>0?'var(--ims-success)':'var(--ims-text-muted)'}">${a.remaining_qty||0}</td>
          <td>${util().formatDate(a.allocation_date)}</td>
        </tr>`).join('')||'<tr><td colspan="5" class="ims__table-empty">No stock allocated yet</td></tr>'}
        </tbody>
      </table>
      <hr class="ims__divider">
      <strong style="font-size:13px">Recent orders</strong>
      <table class="ims__table" style="margin-top:8px">
        <thead><tr><th>Order #</th><th>End customer</th><th>Postcode</th><th>Delivery</th><th>Value</th><th>Status</th></tr></thead>
        <tbody>${(orders||[]).map(o=>`<tr>
          <td>${util().sanitize(o.order_number)}</td>
          <td>${util().sanitize(o.end_customer_name||'—')}</td>
          <td>${util().sanitize(o.end_postcode||'—')}</td>
          <td><span class="ims__pill ims__pill--${o.delivery_method==='mdf_delivers'?'info':'neutral'}" style="font-size:10px">${o.delivery_method==='mdf_delivers'?'We deliver':'Self-arrange'}</span></td>
          <td>${util().formatCurrency(o.total_value)}</td>
          <td>${util().pill(o.status)}</td>
        </tr>`).join('')||'<tr><td colspan="6" class="ims__table-empty">No orders yet</td></tr>'}
        </tbody>
      </table>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.IMS_DROPSHIP.showCustomerForm('${c.id}')"><i class="ti ti-edit"></i> Edit</button>
      ${window.SM.AUTH.isAdmin()?`<button class="ims__btn ims__btn--danger" onclick="window.IMS_DROPSHIP.deleteCustomer('${c.id}','${util().sanitize(c.business_name)}')"><i class="ti ti-trash"></i> Delete customer</button>`:''}
      <button class="ims__btn ims__btn--secondary" onclick="window.IMS_DROPSHIP.showAllocateForm('${c.id}','${util().sanitize(c.business_name)}')"><i class="ti ti-box"></i> Allocate stock</button>
      <button class="ims__btn ims__btn--secondary" onclick="window.IMS_DROPSHIP.showCustomerPricing('${c.id}','${util().sanitize(c.business_name)}')"><i class="ti ti-tag"></i> Custom pricing</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_DROPSHIP.showNewOrder('${c.id}','${util().sanitize(c.business_name)}',${c.discount_pct||0},'${c.delivery_method}')"><i class="ti ti-plus"></i> New order</button>
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Close</button>
    `,'xl');
  }

  async function showCustomerForm(existingId=null){
    let c=null;
    if(existingId){
      const {data}=await db().select('dropship_customers',{eq:{id:existingId},maybeSingle:true,noCompanyFilter:true});
      c=data;
    }
    ui().openModal(existingId?`Edit: ${c?.business_name}`:'Add dropship customer',`
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full"><label class="ims__label ims__label--req">Business name</label><input class="ims__input" type="text" id="ims-dsc-name" value="${util().sanitize(c?.business_name||'')}"></div>
        <div class="ims__form-group"><label class="ims__label">Contact name</label><input class="ims__input" type="text" id="ims-dsc-contact" value="${util().sanitize(c?.contact_name||'')}"></div>
        <div class="ims__form-group"><label class="ims__label">Email</label><input class="ims__input" type="email" id="ims-dsc-email" value="${util().sanitize(c?.email||'')}"></div>
        <div class="ims__form-group"><label class="ims__label">Phone</label><input class="ims__input" type="tel" id="ims-dsc-phone" value="${util().sanitize(c?.phone||'')}"></div>
        <div class="ims__form-group"><label class="ims__label">Postcode</label><input class="ims__input" type="text" id="ims-dsc-postcode" value="${util().sanitize(c?.postcode||'')}"></div>
        <div class="ims__form-group ims__form-group--full"><label class="ims__label">Address</label><textarea class="ims__textarea" id="ims-dsc-address" rows="2">${util().sanitize(c?.address||'')}</textarea></div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Discount off wholesale (%)</label>
          <input class="ims__input" type="number" step="0.1" min="0" max="100" id="ims-dsc-discount" value="${c?.discount_pct||0}">
          <span class="ims__input-hint">e.g. 10 = customer pays wholesale price minus 10%</span>
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Delivery method</label>
          <select class="ims__select" id="ims-dsc-delivery">
            <option value="mdf_delivers" ${c?.delivery_method==='mdf_delivers'||!c?'selected':''}>MDF London delivers to end customer</option>
            <option value="self_arrange" ${c?.delivery_method==='self_arrange'?'selected':''}>Customer self-arranges delivery</option>
          </select>
        </div>
        <div class="ims__form-group"><label class="ims__label">Credit terms</label>
          <select class="ims__select" id="ims-dsc-terms">
            ${['Fortnightly','30 days net','60 days net','Advance payment'].map(t=>`<option ${c?.credit_terms===t?'selected':''}>${t}</option>`).join('')}
          </select>
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Credit limit (£)</label>
          <input class="ims__input" type="number" step="0.01" min="0" id="ims-dsc-limit" value="${c?.credit_limit||''}" placeholder="0 = no limit">
          <span class="ims__input-hint">Maximum outstanding balance allowed before orders are blocked</span>
        </div>
        <div class="ims__form-group ims__form-group--full"><label class="ims__label">Notes</label><textarea class="ims__textarea" id="ims-dsc-notes" rows="2">${util().sanitize(c?.notes||'')}</textarea></div>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_DROPSHIP.saveCustomer('${existingId||''}')"><i class="ti ti-check"></i> ${existingId?'Save changes':'Add customer'}</button>
    `,'lg');
  }

  async function saveCustomer(existingId){
    const restore=_withBtnSpin(event,'Saving...');
    try{
    const g=id=>document.getElementById(id)?.value?.trim()||'';
    const name=g('ims-dsc-name');
    if(!name){ui().toast('Business name is required','error');return;}
    const data={business_name:name,contact_name:g('ims-dsc-contact')||null,email:g('ims-dsc-email')||null,phone:g('ims-dsc-phone')||null,postcode:g('ims-dsc-postcode')||null,address:g('ims-dsc-address')||null,discount_pct:parseFloat(g('ims-dsc-discount'))||0,delivery_method:g('ims-dsc-delivery')||'mdf_delivers',credit_terms:g('ims-dsc-terms')||'Fortnightly',credit_limit:parseFloat(g('ims-dsc-limit'))||0,notes:g('ims-dsc-notes')||null};
    const {error}=existingId?await db().update('dropship_customers',existingId,data):await db().insert('dropship_customers',data);
    if(error){ui().toast('Failed: '+error.message,'error');return;}
    ui().closeModal();ui().toast(`Dropship customer ${existingId?'updated':'added'}`,'success');loadCustomers();
    } finally { restore(); }
  }

  async function deleteCustomer(customerId, customerName) {
    if (!window.SM.AUTH.isAdmin()) { ui().toast('Admin access required', 'error'); return; }

    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);

    const [{ data: allocs }, { data: orders }, { data: invoices }] = await Promise.all([
      client.from('dropship_allocations').select('id, remaining_qty, product_id, warehouse_id').eq('customer_id', customerId),
      client.from('orders').select('id, order_number, status, invoice_id').eq('dropship_customer_id', customerId).eq('fulfillment_type', 'dropship'),
      client.from('dropship_invoices').select('id').eq('customer_id', customerId)
    ]);

    const totalReserved = (allocs || []).reduce((s, a) => s + (a.remaining_qty || 0), 0);
    const totalOrders = (orders || []).length;
    const totalInvoices = (invoices || []).length;

    ui().openModal('Delete customer', `
      <div class="ims__alert ims__alert--danger" style="margin-bottom:16px">
        <i class="ti ti-alert-triangle"></i>
        <span>You are about to permanently delete <strong>${util().sanitize(customerName)}</strong>. This cannot be undone.</span>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:16px">
        <div class="ims__stat" style="padding:12px">
          <div class="ims__stat-label">Stock allocations</div>
          <div class="ims__stat-value" style="font-size:22px">${(allocs||[]).length}</div>
          <div style="font-size:11px;color:var(--ims-text-muted);margin-top:2px">${totalReserved} reserved units will be released</div>
        </div>
        <div class="ims__stat" style="padding:12px">
          <div class="ims__stat-label">Dropship orders</div>
          <div class="ims__stat-value" style="font-size:22px">${totalOrders}</div>
          <div style="font-size:11px;color:var(--ims-text-muted);margin-top:2px">All order records will be removed</div>
        </div>
        <div class="ims__stat" style="padding:12px">
          <div class="ims__stat-label">Invoices</div>
          <div class="ims__stat-value" style="font-size:22px">${totalInvoices}</div>
          <div style="font-size:11px;color:var(--ims-text-muted);margin-top:2px">All invoices will be deleted</div>
        </div>
        <div class="ims__stat" style="padding:12px">
          <div class="ims__stat-label">Financial transactions</div>
          <div class="ims__stat-value" style="font-size:22px">All</div>
          <div style="font-size:11px;color:var(--ims-text-muted);margin-top:2px">Linked transactions removed</div>
        </div>
      </div>
      <div style="font-size:13px;color:var(--ims-text-muted)">Activity logs, manifest items, logistics jobs and COD records will also be removed.</div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--danger" onclick="window.IMS_DROPSHIP.confirmDeleteCustomer('${customerId}','${util().sanitize(customerName)}')">
        <i class="ti ti-trash"></i> Yes, delete everything
      </button>
    `, 'sm');
  }

  async function confirmDeleteCustomer(customerId, customerName) {
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);

    const [{ data: allocs }, { data: orders }] = await Promise.all([
      client.from('dropship_allocations').select('id, remaining_qty, product_id, warehouse_id').eq('customer_id', customerId),
      client.from('orders').select('id, order_number, status').eq('dropship_customer_id', customerId).eq('fulfillment_type', 'dropship')
    ]);

    const orderIds = (orders || []).map(o => o.id);

    ui().closeModal();
    ui().toast('Deleting customer — please wait...', 'info', 8000);

    // 1 — Release stock reservations from all allocations
    if (allocs?.length) {
      await Promise.all(allocs.map(async alloc => {
        if (!alloc.remaining_qty || !alloc.product_id || !alloc.warehouse_id) return;
        const { data: sl } = await db().select('stock_levels', {
          eq: { product_id: alloc.product_id, warehouse_id: alloc.warehouse_id },
          maybeSingle: true, noCompanyFilter: true
        });
        if (sl) {
          await db().update('stock_levels', sl.id, {
            reserved_by_allocations: Math.max(0, (sl.reserved_by_allocations || 0) - alloc.remaining_qty)
          });
        }
      }));
    }

    // 2 — Restore stock quantity for delivered/collected orders
    const deliveredOrders = (orders || []).filter(o => ['collected', 'delivered'].includes(o.status));
    if (deliveredOrders.length) {
      const { data: allOrderItems } = await client.from('order_items')
        .select('product_id, quantity')
        .in('order_id', deliveredOrders.map(o => o.id));
      if (allOrderItems?.length) {
        await Promise.all(allOrderItems.map(async item => {
          if (!item.product_id) return;
          const { data: sl } = await db().select('stock_levels', {
            eq: { product_id: item.product_id }, maybeSingle: true, noCompanyFilter: true
          });
          if (sl) {
            await db().update('stock_levels', sl.id, {
              quantity: (sl.quantity || 0) + (item.quantity || 0)
            });
          }
        }));
      }
    }

    // 3 — Delete transactions, AR, manifest items, logistics, COD, activity logs, order items
    if (orderIds.length) {
      for (const orderId of orderIds) {
        await client.from('transactions').delete().eq('related_id', orderId);
        await client.from('accounts_receivable').delete().eq('order_id', orderId);
        await client.from('manifest_items').delete().eq('order_id', orderId);
        await client.from('logistics_jobs').delete().eq('order_id', orderId);
        await client.from('cod_transactions').delete().eq('order_id', orderId);
        await client.from('order_activity_log').delete().eq('order_id', orderId);
      }
      const orderNumbers = (orders || []).map(o => o.order_number);
      for (const ref of orderNumbers) {
        await client.from('transactions').delete().eq('reference', ref);
      }
      await client.from('order_items').delete().in('order_id', orderIds);
      await client.from('orders').delete().in('id', orderIds);
    }

    // 4 — Delete allocations and invoices
    await client.from('dropship_allocations').delete().eq('customer_id', customerId);
    await client.from('dropship_invoices').delete().eq('customer_id', customerId);

    // 5 — Delete the customer
    await db().remove('dropship_customers', customerId);

    ui().toast(`${customerName} deleted — all records removed and stock released`, 'success', 6000);
    loadCustomers();
  }

  // ═══════════════════════════════════════════════════════════════
  // STOCK ALLOCATIONS — BULK MULTI-PRODUCT FLOW
  // ═══════════════════════════════════════════════════════════════
  let _allocSelection = {};   // { productId: { id, name, sku, stockByWh } }
  let _allocCustomerId = '';
  let _allocCustomerName = '';
  let _allocWarehouses = [];
  let _allocAllProducts = [];

  // STEP 1 — search & select products
  // ─── DROPSHIP CUSTOMER-SPECIFIC FLAT PRICING ────────────────────
  // Per explicit design decision: dropship customers get a flat
  // per-unit price set directly for them, per product — completely
  // decoupled from the shared wholesale price table used by regular
  // retailers/wholesalers. Any standing % discount on the customer's
  // record is deducted from THIS flat price (via _dsCalcPrice, already
  // in place), not from the shared wholesale rate. When no override
  // exists for a given product, pricing falls back to the shared
  // wholesale rate exactly as before — this is purely additive.
  async function showCustomerPricing(customerId, customerName){
    const btn=event?.target?.closest?.('button');
    const restore=_withBtnSpin(btn,'Loading...');
    const [{data:rows},{data:allocs}]=await Promise.all([
      db().select('dropship_customer_prices',{
        select:'id,product_id,unit_price,updated_at,products(name,sku)',
        eq:{customer_id:customerId},
        order:{col:'created_at',asc:false}
      }),
      // Only products actually allocated to this customer should be
      // selectable — per request, not the entire product catalogue.
      db().select('dropship_allocations',{
        select:'product_id,products(name,sku)',
        eq:{customer_id:customerId}
      })
    ]);
    restore();

    const alreadyPriced=new Set((rows||[]).map(r=>r.product_id));
    const uniqueAllocs=(allocs||[]).filter((a,i,arr)=>arr.findIndex(x=>x.product_id===a.product_id)===i && !alreadyPriced.has(a.product_id));
    const productOpts=uniqueAllocs.length
      ? uniqueAllocs.map(a=>`<option value="${a.product_id}">${util().sanitize(a.products?.name||'—')}${a.products?.sku?' ('+util().sanitize(a.products.sku)+')':''}</option>`).join('')
      : '';

    ui().openModal(`Custom pricing: ${customerName}`,`
      <div class="ims__alert ims__alert--info" style="margin-bottom:12px">
        <i class="ti ti-info-circle"></i>
        <span>Set a flat per-unit price for this customer, per product. Any standing discount % on their record is deducted from this price. Products without an override here use the standard shared wholesale rate.</span>
      </div>
      <div id="ims-cp-list" style="margin-bottom:14px">
        ${(rows||[]).length?`
          <table class="ims__table">
            <thead><tr><th>Product</th><th>SKU</th><th style="text-align:right">Flat price</th><th></th><th></th></tr></thead>
            <tbody>${rows.map(r=>{
              const isRecent=r.updated_at&&(Date.now()-new Date(r.updated_at).getTime())<5*60*1000;
              return `
              <tr style="${isRecent?'background:var(--ims-warning-bg)':''}">
                <td>${util().sanitize(r.products?.name||'—')}</td>
                <td style="font-size:11px;color:var(--ims-text-muted)">${util().sanitize(r.products?.sku||'—')}</td>
                <td style="text-align:right;font-weight:600">${util().formatCurrency(r.unit_price)}</td>
                <td>${isRecent?'<span class="ims__pill ims__pill--warning" style="font-size:10px">Just updated</span>':''}</td>
                <td><div class="ims__btn-group">
                  <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_DROPSHIP.editCustomPrice('${r.product_id}','${util().sanitize((r.products?.name||'').replace(/'/g,"\\'"))}',${r.unit_price})" title="Edit price"><i class="ti ti-edit"></i></button>
                  <button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon" onclick="window.IMS_DROPSHIP.deleteCustomPrice('${r.id}','${customerId}','${util().sanitize(customerName)}')" title="Remove"><i class="ti ti-trash"></i></button>
                </div></td>
              </tr>`;
            }).join('')}
            </tbody>
          </table>` : '<div style="font-size:13px;color:var(--ims-text-muted);text-align:center;padding:16px">No custom prices set yet for this customer</div>'}
      </div>
      <hr class="ims__divider">
      <div style="font-size:13px;font-weight:600;margin-bottom:8px">Add / update a custom price</div>
      <input type="hidden" id="ims-cp-editing-product">
      <div id="ims-cp-editing-label" style="display:none;font-size:12px;color:var(--ims-accent);margin-bottom:6px"></div>
      ${productOpts?`
        <div style="display:flex;gap:8px;align-items:center">
          <select class="ims__select" id="ims-cp-product">
            <option value="">Select an allocated product...</option>
            ${productOpts}
          </select>
          <input class="ims__input" type="number" step="0.01" min="0" id="ims-cp-price" placeholder="Flat unit price (£)" style="max-width:160px">
          <button class="ims__btn ims__btn--primary ims__btn--sm" onclick="window.IMS_DROPSHIP.saveCustomPrice('${customerId}','${util().sanitize(customerName)}')"><i class="ti ti-check"></i> Save</button>
        </div>`
        : `<div style="display:flex;gap:8px;align-items:center">
             <input class="ims__input" type="number" step="0.01" min="0" id="ims-cp-price" placeholder="Flat unit price (£)" style="max-width:160px" disabled>
             <button class="ims__btn ims__btn--primary ims__btn--sm" onclick="window.IMS_DROPSHIP.saveCustomPrice('${customerId}','${util().sanitize(customerName)}')" disabled><i class="ti ti-check"></i> Save</button>
           </div>
           <div class="ims__input-hint" style="margin-top:6px">All allocated products already have a custom price — use the <i class="ti ti-edit"></i> edit button above to change one, or allocate additional stock to price something new.</div>`}
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Close</button>
    `,'lg');
  }

  function editCustomPrice(productId, productName, currentPrice){
    const hidden=document.getElementById('ims-cp-editing-product');
    const label=document.getElementById('ims-cp-editing-label');
    const priceInput=document.getElementById('ims-cp-price');
    if(hidden) hidden.value=productId;
    if(label){ label.style.display=''; label.innerHTML=`<i class="ti ti-edit"></i> Editing: <strong>${productName}</strong>`; }
    if(priceInput){ priceInput.disabled=false; priceInput.value=currentPrice; priceInput.focus(); }
  }

  async function saveCustomPrice(customerId,customerName){
    const btn=event?.target?.closest?.('button');
    const restore=_withBtnSpin(btn,'Saving...');
    // Editing an existing row takes priority over the dropdown, since
    // already-priced products are deliberately excluded from it — the
    // Edit button is the only path to updating a price once it's set.
    const editingId=document.getElementById('ims-cp-editing-product')?.value;
    const productId=editingId||document.getElementById('ims-cp-product')?.value;
    if(!productId){ui().toast('Please select a product','error');restore();return;}
    const price=parseFloat(document.getElementById('ims-cp-price')?.value);
    if(!price||price<=0){ui().toast('Please enter a valid price','error');restore();return;}

    const {data:existing}=await db().select('dropship_customer_prices',{
      eq:{customer_id:customerId,product_id:productId},maybeSingle:true
    });
    let error;
    if(existing){
      ({error}=await db().update('dropship_customer_prices',existing.id,{unit_price:price,updated_at:new Date().toISOString()}));
    } else {
      ({error}=await db().insert('dropship_customer_prices',{customer_id:customerId,product_id:productId,unit_price:price}));
    }
    restore();
    if(error){ui().toast('Failed: '+error.message,'error');return;}
    ui().toast('Custom price saved','success');
    showCustomerPricing(customerId,customerName);
  }

  async function deleteCustomPrice(id,customerId,customerName){
    ui().confirm(
      'Remove this custom price? The product will fall back to the standard shared wholesale rate for this customer.',
      async()=>{
        await db().remove('dropship_customer_prices',id);
        ui().toast('Custom price removed','success');
        showCustomerPricing(customerId,customerName);
      },
      'Remove price', true
    );
  }

  async function showAllocateForm(customerId, customerName){
    _allocCustomerId   = customerId;
    _allocCustomerName = customerName;
    _allocSelection    = {};

    const [{data:whs},{data:prods}]=await Promise.all([
      db().select('warehouses',{eq:{is_active:true}}),
      db().select('products',{select:'id,name,sku',order:{col:'name'}})
    ]);
    _allocWarehouses  = whs  || [];
    _allocAllProducts = prods || [];

    ui().openModal(
      `Allocate stock: ${customerName}`,
      `<div class="ims__alert ims__alert--info" style="margin-bottom:12px">
        <i class="ti ti-info-circle"></i>
        <span>Search by product name or SKU, tick to select multiple products, then click <strong>Next: set quantities</strong>.</span>
      </div>
      <div class="ims__search-wrap" style="margin-bottom:10px">
        <i class="ti ti-search"></i>
        <input class="ims__input" type="text" id="ims-alloc-search"
          placeholder="Type product name or SKU..."
          oninput="window.IMS_DROPSHIP.renderAllocSearch(this.value)"
          autocomplete="off">
      </div>
      <div id="ims-alloc-chips" style="display:flex;flex-wrap:wrap;gap:6px;min-height:24px;margin-bottom:10px"></div>
      <div id="ims-alloc-results" style="max-height:360px;overflow-y:auto;border:1px solid var(--ims-border);border-radius:var(--ims-radius)">
        <div style="padding:28px;text-align:center;color:var(--ims-text-muted);font-size:13px">
          <i class="ti ti-search" style="font-size:28px;display:block;margin-bottom:8px;opacity:.25"></i>
          Start typing to search products
        </div>
      </div>
      <div id="ims-alloc-count" style="font-size:12px;color:var(--ims-text-muted);margin-top:8px;text-align:right">0 products selected</div>`,
      `<button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
       <button class="ims__btn ims__btn--primary" id="ims-alloc-next-btn" disabled
         onclick="window.IMS_DROPSHIP.allocConfirmSelection()">
         <i class="ti ti-arrow-right"></i> Next: set quantities
       </button>`,
      'lg'
    );
    setTimeout(()=>document.getElementById('ims-alloc-search')?.focus(),150);
  }

  function renderAllocSearch(query){
    const q=(query||'').toLowerCase().trim();
    const resultsEl=document.getElementById('ims-alloc-results');
    if(!resultsEl)return;

    if(!q){
      resultsEl.innerHTML=`<div style="padding:28px;text-align:center;color:var(--ims-text-muted);font-size:13px">
        <i class="ti ti-search" style="font-size:28px;display:block;margin-bottom:8px;opacity:.25"></i>
        Start typing to search products
      </div>`;
      return;
    }

    const filtered=_allocAllProducts.filter(p=>
      p.name?.toLowerCase().includes(q)||p.sku?.toLowerCase().includes(q)
    );

    if(!filtered.length){
      resultsEl.innerHTML=`<div style="padding:16px;text-align:center;color:var(--ims-text-muted);font-size:13px">No products match "${util().sanitize(query)}"</div>`;
      return;
    }

    resultsEl.innerHTML=filtered.slice(0,60).map(p=>{
      const selected=!!_allocSelection[p.id];
      return`<label style="display:flex;align-items:center;gap:10px;padding:9px 14px;
        border-bottom:1px solid var(--ims-border);cursor:pointer;
        background:${selected?'var(--ims-accent-bg)':'var(--ims-surface)'};transition:background .1s"
        onmouseenter="this.style.background='${selected?'var(--ims-accent-bg)':'var(--ims-surface2)'}'"
        onmouseleave="this.style.background='${selected?'var(--ims-accent-bg)':'var(--ims-surface)'}'">
        <input type="checkbox" ${selected?'checked':''}
          onchange="window.IMS_DROPSHIP.allocToggleProduct('${p.id}','${util().sanitize(p.name).replace(/'/g,"\\'")}','${util().sanitize(p.sku||'')}',this.checked)"
          style="width:16px;height:16px;flex-shrink:0;accent-color:var(--ims-accent)">
        <div style="flex:1;min-width:0">
          <div style="font-size:13px;font-weight:${selected?'600':'400'};color:${selected?'var(--ims-accent)':'var(--ims-text)'};white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${util().sanitize(p.name)}</div>
          <div style="font-size:11px;color:var(--ims-text-muted)">${util().sanitize(p.sku||'—')}</div>
        </div>
        ${selected?'<i class="ti ti-check" style="color:var(--ims-accent);font-size:16px;flex-shrink:0"></i>':''}
      </label>`;
    }).join('')+(filtered.length>60?`<div style="padding:8px 14px;font-size:11px;color:var(--ims-text-muted);text-align:center;background:var(--ims-surface2)">Showing first 60 — refine your search</div>`:'');
  }

  function allocToggleProduct(id,name,sku,checked){
    if(checked){
      _allocSelection[id]={id,name,sku,stockByWh:{}};
    } else {
      delete _allocSelection[id];
    }
    // Update chips
    const chipsEl=document.getElementById('ims-alloc-chips');
    const countEl=document.getElementById('ims-alloc-count');
    const nextBtn=document.getElementById('ims-alloc-next-btn');
    const count=Object.keys(_allocSelection).length;

    if(chipsEl){
      chipsEl.innerHTML=Object.values(_allocSelection).map(p=>`
        <span style="display:inline-flex;align-items:center;gap:5px;padding:3px 8px 3px 10px;
          background:var(--ims-accent-bg);color:var(--ims-accent);border-radius:999px;font-size:12px;font-weight:500">
          ${util().sanitize(p.name)}
          <button onclick="window.IMS_DROPSHIP.allocToggleProduct('${p.id}','','',false);window.IMS_DROPSHIP.renderAllocSearch(document.getElementById('ims-alloc-search')?.value||'')"
            style="background:none;border:none;color:var(--ims-accent);cursor:pointer;padding:0;line-height:1;font-size:15px" title="Remove">×</button>
        </span>`).join('');
    }
    if(countEl) countEl.textContent=`${count} product${count===1?'':'s'} selected`;
    if(nextBtn) nextBtn.disabled=count===0;

    // Re-render results to reflect new state
    renderAllocSearch(document.getElementById('ims-alloc-search')?.value||'');
  }

  // STEP 2 — set warehouse + quantity per selected product
  async function allocConfirmSelection(){
    const selected=Object.values(_allocSelection);
    if(!selected.length)return;

    // Load available stock for all selected products
    const enriched=await Promise.all(selected.map(async p=>{
      const {data:levels}=await db().select('stock_levels',{
        select:'warehouse_id,quantity,reserved_qty,reserved_by_allocations',
        eq:{product_id:p.id},
        noCompanyFilter:true
      });
      const byWh={};
      (levels||[]).forEach(l=>{byWh[l.warehouse_id]=(l.quantity||0)-(l.reserved_qty||0)-(l.reserved_by_allocations||0);});
      return{...p,stockByWh:byWh};
    }));
    enriched.forEach(p=>{_allocSelection[p.id]=p;});

    const defaultWhId=_allocWarehouses[0]?.id||'';

    ui().openModal(
      `Allocate stock: ${_allocCustomerName} — Set quantities`,
      `<div class="ims__alert ims__alert--info" style="margin-bottom:14px">
        <i class="ti ti-info-circle"></i>
        <span>Set the warehouse and quantity for each product. Available stock updates when you change warehouse. Stock will be <strong>soft reserved</strong> — stays in your warehouse but shows as reserved.</span>
      </div>
      <div class="ims__table-wrap">
        <table class="ims__table">
          <thead><tr>
            <th>Product</th><th>SKU</th>
            <th style="width:190px">Warehouse</th>
            <th style="width:110px">Available</th>
            <th style="width:110px">Qty to allocate</th>
            <th style="width:40px"></th>
          </tr></thead>
          <tbody id="ims-alloc-qty-tbody">
            ${enriched.map(p=>{
              const avail=p.stockByWh[defaultWhId]??0;
              return`<tr id="ims-alloc-row-${p.id}">
                <td style="font-weight:500">${util().sanitize(p.name)}</td>
                <td style="font-size:12px;color:var(--ims-text-muted)">${util().sanitize(p.sku||'—')}</td>
                <td>
                  <select class="ims__select" style="font-size:12px" id="ims-alloc-wh-${p.id}"
                    onchange="window.IMS_DROPSHIP.allocUpdateAvail('${p.id}')">
                    ${_allocWarehouses.map(w=>`<option value="${w.id}">${util().sanitize(w.name)}</option>`).join('')}
                  </select>
                </td>
                <td>
                  <span id="ims-alloc-avail-${p.id}"
                    style="font-weight:600;color:${avail<=0?'var(--ims-danger)':avail<=5?'var(--ims-warning)':'var(--ims-success)'}">
                    ${avail}
                  </span>
                </td>
                <td>
                  <input class="ims__input" type="number" min="1"
                    id="ims-alloc-qty-${p.id}" value="1"
                    style="width:80px;font-size:13px;text-align:center"
                    oninput="window.IMS_DROPSHIP.allocValidateQty('${p.id}')">
                </td>
                <td>
                  <button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon"
                    onclick="window.IMS_DROPSHIP.allocRemoveRow('${p.id}')" title="Remove">
                    <i class="ti ti-trash"></i>
                  </button>
                </td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-top:12px">
        <div style="font-size:13px;color:var(--ims-text-muted)">
          <strong id="ims-alloc-total-count">${enriched.length}</strong> product(s) to allocate
        </div>
      </div>`,
      `<button class="ims__btn ims__btn--ghost"
          onclick="window.IMS_DROPSHIP.showAllocateForm('${_allocCustomerId}','${_allocCustomerName}')">
          <i class="ti ti-arrow-left"></i> Back
        </button>
        <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
        <button class="ims__btn ims__btn--primary" onclick="window.IMS_DROPSHIP.saveAllocationBulk()">
          <i class="ti ti-check"></i> Allocate all stock
        </button>`,
      'xl'
    );
  }

  function allocUpdateAvail(productId){
    const whId=document.getElementById(`ims-alloc-wh-${productId}`)?.value;
    const avail=_allocSelection[productId]?.stockByWh?.[whId]??0;
    const el=document.getElementById(`ims-alloc-avail-${productId}`);
    if(el){
      el.textContent=avail;
      el.style.color=avail<=0?'var(--ims-danger)':avail<=5?'var(--ims-warning)':'var(--ims-success)';
    }
    allocValidateQty(productId);
  }

  function allocValidateQty(productId){
    const whId=document.getElementById(`ims-alloc-wh-${productId}`)?.value;
    const qtyEl=document.getElementById(`ims-alloc-qty-${productId}`);
    const avail=_allocSelection[productId]?.stockByWh?.[whId]??0;
    const qty=parseInt(qtyEl?.value)||0;
    if(qtyEl) qtyEl.style.borderColor=(qty>avail||qty<1)?'var(--ims-danger)':'';
  }

  function allocRemoveRow(productId){
    document.getElementById(`ims-alloc-row-${productId}`)?.remove();
    delete _allocSelection[productId];
    const count=Object.keys(_allocSelection).length;
    const countEl=document.getElementById('ims-alloc-total-count');
    if(countEl) countEl.textContent=count;
    if(count===0){
      ui().toast('No products left — returning to search','info');
      showAllocateForm(_allocCustomerId,_allocCustomerName);
    }
  }

  async function saveAllocationBulk(){
    const rows=Object.keys(_allocSelection);
    const total=rows.length;
    const today=new Date().toISOString().split('T')[0];
    let errors=[],saved=0;

    // Validate all rows first before touching the DB
    for(const productId of rows){
      const whId=document.getElementById(`ims-alloc-wh-${productId}`)?.value;
      const qty=parseInt(document.getElementById(`ims-alloc-qty-${productId}`)?.value)||0;
      const avail=_allocSelection[productId]?.stockByWh?.[whId]??0;
      const name=_allocSelection[productId]?.name||productId;
      if(!whId){errors.push(`${name}: no warehouse selected`);}
      else if(qty<1){errors.push(`${name}: quantity must be at least 1`);}
      else if(qty>avail){errors.push(`${name}: only ${avail} units available`);}
    }
    if(errors.length){
      ui().toast(errors[0],'error',5000);
      return;
    }

    // Disable button and show progress bar in modal footer
    const saveBtn=document.querySelector('#ims-modal-footer .ims__btn--primary');
    if(saveBtn){
      saveBtn.disabled=true;
      saveBtn.innerHTML=`<i class="ti ti-loader-2 ims__spin"></i> Allocating...`;
    }

    // Inject a progress bar above the table
    const tbody=document.getElementById('ims-alloc-qty-tbody');
    if(tbody){
      const progressHtml=`
        <div id="ims-alloc-progress" style="margin-bottom:14px">
          <div style="display:flex;justify-content:space-between;font-size:12px;color:var(--ims-text-muted);margin-bottom:5px">
            <span id="ims-alloc-progress-label">Starting...</span>
            <span id="ims-alloc-progress-pct">0%</span>
          </div>
          <div style="background:var(--ims-border);border-radius:999px;height:8px;overflow:hidden">
            <div id="ims-alloc-progress-bar"
              style="height:8px;width:0%;background:var(--ims-accent);border-radius:999px;transition:width .25s"></div>
          </div>
        </div>`;
      tbody.closest('.ims__table-wrap').insertAdjacentHTML('beforebegin', progressHtml);
    }

    const setProgress=(done,label)=>{
      const pct=Math.round((done/total)*100);
      const bar=document.getElementById('ims-alloc-progress-bar');
      const pctEl=document.getElementById('ims-alloc-progress-pct');
      const labelEl=document.getElementById('ims-alloc-progress-label');
      if(bar) bar.style.width=pct+'%';
      if(pctEl) pctEl.textContent=pct+'%';
      if(labelEl) labelEl.textContent=label;
    };

    for(let i=0;i<rows.length;i++){
      const productId=rows[i];
      const whId=document.getElementById(`ims-alloc-wh-${productId}`)?.value;
      const qty=parseInt(document.getElementById(`ims-alloc-qty-${productId}`)?.value)||0;
      const name=_allocSelection[productId]?.name||productId;

      setProgress(i, `Allocating ${i+1} of ${total}: ${name}`);

      const {error:allocErr}=await db().insert('dropship_allocations',{
        customer_id:_allocCustomerId,
        product_id:productId,
        warehouse_id:whId,
        allocated_qty:qty,
        remaining_qty:qty,
        allocation_date:today,
        created_by:window.SM.AUTH.user?.id
      });
      if(allocErr){errors.push(`${name}: ${allocErr.message}`);continue;}

      // Reserve stock
      const {data:sl}=await db().select('stock_levels',{
        eq:{product_id:productId,warehouse_id:whId},
        maybeSingle:true,noCompanyFilter:true
      });
      if(sl) await db().update('stock_levels',sl.id,{reserved_by_allocations:(sl.reserved_by_allocations||0)+qty});

      saved++;
      setProgress(i+1, `Done: ${name}`);

      // Dim the completed row so user sees progress visually
      const row=document.getElementById(`ims-alloc-row-${productId}`);
      if(row) row.style.opacity='0.4';
    }

    setProgress(total,'All done!');
    await new Promise(r=>setTimeout(r,400)); // brief pause so user sees 100%

    if(errors.length){
      ui().toast(`${saved} allocated, ${errors.length} failed — ${errors[0]}`,'warning',6000);
      if(saveBtn){saveBtn.disabled=false;saveBtn.innerHTML='<i class="ti ti-check"></i> Allocate all stock';}
    } else {
      ui().closeModal();
      ui().toast(`${saved} product${saved===1?'':'s'} allocated to ${_allocCustomerName}`,'success',5000);
    }
    _allocSelection={};
    loadCustomers();
  }

  // kept for backwards compat (openCustomer footer button)
  function checkAvailableStock(){}

  async function loadAllocations(){
    const content=document.getElementById('ims-ds-content');
    if(!content)return;
    const actions=document.getElementById('ims-ds-actions');
    if(actions)actions.innerHTML='';

    const {data:allocs}=await db().select('dropship_allocations',{
      select:'*,dropship_customers(business_name),products(name,sku),warehouses(name)',
      order:{col:'created_at',asc:false}
    });
    const items=allocs||[];
    content.innerHTML=`
      <div class="ims__table-wrap">
        <table class="ims__table">
          <thead><tr><th>Customer</th><th>Product</th><th>SKU</th><th>Warehouse</th><th>Allocated</th><th>Remaining</th><th>Sold</th><th>Date</th><th></th></tr></thead>
          <tbody>${items.map(a=>`<tr>
            <td><strong>${util().sanitize(a.dropship_customers?.business_name||'—')}</strong></td>
            <td>${util().sanitize(a.products?.name||'—')}</td>
            <td>${util().sanitize(a.products?.sku||'—')}</td>
            <td>${util().sanitize(a.warehouses?.name||'—')}</td>
            <td>${a.allocated_qty||0}</td>
            <td style="font-weight:600;color:${(a.remaining_qty||0)>0?'var(--ims-success)':'var(--ims-text-muted)'}">${a.remaining_qty||0}</td>
            <td>${(a.allocated_qty||0)-(a.remaining_qty||0)}</td>
            <td>${util().formatDate(a.allocation_date)}</td>
            <td>
              <div class="ims__btn-group">
                <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon"
                  onclick="window.IMS_DROPSHIP.editAllocation('${a.id}')"
                  title="Edit allocation"><i class="ti ti-edit"></i></button>
                <button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon"
                  onclick="window.IMS_DROPSHIP.recallAllocation('${a.id}',${a.remaining_qty||0},'${a.product_id}','${a.warehouse_id}')"
                  title="Recall all remaining stock"><i class="ti ti-arrow-back-up"></i></button>
              </div>
            </td>
          </tr>`).join('')||'<tr><td colspan="9" class="ims__table-empty">No allocations yet</td></tr>'}
          </tbody>
        </table>
      </div>`;
  }

  async function editAllocation(allocId){
    // Load allocation with related data
    const {data:a}=await db().select('dropship_allocations',{
      select:'*,products(name,sku),warehouses(name)',
      eq:{id:allocId},maybeSingle:true,noCompanyFilter:true
    });
    if(!a)return;

    const sold=(a.allocated_qty||0)-(a.remaining_qty||0);
    const {data:whs}=await db().select('warehouses',{eq:{is_active:true}});

    // Load available stock for each warehouse (excluding this allocation's own reservation)
    const stockByWh={};
    await Promise.all((whs||[]).map(async w=>{
      const {data:sl}=await db().select('stock_levels',{
        eq:{product_id:a.product_id,warehouse_id:w.id},
        maybeSingle:true,noCompanyFilter:true
      });
      if(sl){
        // Add back the current reservation so available = true free + what this allocation holds
        const currentReserved = w.id===a.warehouse_id ? (a.remaining_qty||0) : 0;
        stockByWh[w.id]=(sl.quantity||0)-(sl.reserved_qty||0)-(sl.reserved_by_allocations||0)+currentReserved;
      } else {
        stockByWh[w.id]=0;
      }
    }));

    const whOpts=(whs||[]).map(w=>`
      <option value="${w.id}" ${w.id===a.warehouse_id?'selected':''}>
        ${util().sanitize(w.name)} — ${stockByWh[w.id]??0} available
      </option>`).join('');

    const currentAvail=stockByWh[a.warehouse_id]??0;

    ui().openModal(`Edit allocation: ${util().sanitize(a.products?.name||'—')}`,`
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:16px">
        <div class="ims__stat" style="padding:10px">
          <div class="ims__stat-label">Originally allocated</div>
          <div class="ims__stat-value" style="font-size:20px">${a.allocated_qty||0}</div>
        </div>
        <div class="ims__stat" style="padding:10px">
          <div class="ims__stat-label">Already sold</div>
          <div class="ims__stat-value" style="font-size:20px;color:${sold>0?'var(--ims-text-muted)':''}">${sold}</div>
          <div class="ims__stat-sub">Cannot be reduced below this</div>
        </div>
        <div class="ims__stat" style="padding:10px">
          <div class="ims__stat-label">Remaining (unsold)</div>
          <div class="ims__stat-value ims__stat-value--success" style="font-size:20px">${a.remaining_qty||0}</div>
        </div>
      </div>

      <div class="ims__alert ims__alert--info" style="margin-bottom:14px">
        <i class="ti ti-info-circle"></i>
        <span>
          Changing the <strong>quantity</strong> adjusts the stock reservation automatically.
          Changing the <strong>warehouse</strong> moves the reservation to the new location.
          The <strong>date</strong> is for record-keeping only.
        </span>
      </div>

      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Warehouse</label>
          <select class="ims__select" id="ims-ea-warehouse"
            onchange="window.IMS_DROPSHIP.editAllocUpdateAvail('${allocId}')">
            ${whOpts}
          </select>
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Total allocated quantity</label>
          <input class="ims__input" type="number" min="${sold}" id="ims-ea-qty"
            value="${a.allocated_qty||0}"
            oninput="window.IMS_DROPSHIP.editAllocUpdateAvail('${allocId}')">
          <span class="ims__input-hint">Minimum: ${sold} (already sold). Available in selected warehouse: <strong id="ims-ea-avail">${currentAvail}</strong></span>
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Allocation date</label>
          <input class="ims__input" type="date" id="ims-ea-date"
            value="${a.allocation_date||new Date().toISOString().split('T')[0]}">
        </div>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary"
        onclick="window.IMS_DROPSHIP.saveEditAllocation('${allocId}','${a.product_id}','${a.warehouse_id}',${a.allocated_qty||0},${a.remaining_qty||0},${sold})">
        <i class="ti ti-check"></i> Save changes
      </button>
    `,'sm');

    // Store stock data for the live available check
    window._editAllocStockByWh = stockByWh;
  }

  function editAllocUpdateAvail(){
    const whId=document.getElementById('ims-ea-warehouse')?.value;
    const availEl=document.getElementById('ims-ea-avail');
    const qtyEl=document.getElementById('ims-ea-qty');
    if(!whId||!availEl)return;
    const avail=window._editAllocStockByWh?.[whId]??0;
    availEl.textContent=avail;
    availEl.style.color=avail<=0?'var(--ims-danger)':avail<=5?'var(--ims-warning)':'var(--ims-success)';
    // Validate qty
    const qty=parseInt(qtyEl?.value)||0;
    if(qtyEl) qtyEl.style.borderColor=(qty>avail+qty||qty<0)?'var(--ims-danger)':'';
  }

  async function saveEditAllocation(allocId,productId,oldWhId,oldAllocQty,oldRemainingQty,sold){
    const restore=_withBtnSpin(event,'Saving...');
    try{
    const newWhId=document.getElementById('ims-ea-warehouse')?.value;
    const newAllocQty=parseInt(document.getElementById('ims-ea-qty')?.value)||0;
    const newDate=document.getElementById('ims-ea-date')?.value;

    if(!newWhId){ui().toast('Please select a warehouse','error');return;}
    if(newAllocQty<sold){ui().toast(`Quantity cannot be less than ${sold} (already sold)`,'error');return;}

    // New remaining = new total - sold
    const newRemainingQty=newAllocQty-sold;

    // Check available stock in new warehouse
    // (stockByWh already adds back old reservation, so avail = true free + old reserved)
    const avail=window._editAllocStockByWh?.[newWhId]??0;
    if(newRemainingQty>avail){
      ui().toast(`Only ${avail} units available in selected warehouse`,'error');return;
    }

    // 1 — Release old reservation from old warehouse
    const {data:oldSl}=await db().select('stock_levels',{
      eq:{product_id:productId,warehouse_id:oldWhId},
      maybeSingle:true,noCompanyFilter:true
    });
    if(oldSl){
      await db().update('stock_levels',oldSl.id,{
        reserved_by_allocations:Math.max(0,(oldSl.reserved_by_allocations||0)-oldRemainingQty)
      });
    }

    // 2 — Apply new reservation to new warehouse
    const {data:newSl}=await db().select('stock_levels',{
      eq:{product_id:productId,warehouse_id:newWhId},
      maybeSingle:true,noCompanyFilter:true
    });
    if(newSl){
      await db().update('stock_levels',newSl.id,{
        reserved_by_allocations:(newSl.reserved_by_allocations||0)+newRemainingQty
      });
    }

    // 3 — Update the allocation record
    const {error}=await db().update('dropship_allocations',allocId,{
      warehouse_id:newWhId,
      allocated_qty:newAllocQty,
      remaining_qty:newRemainingQty,
      allocation_date:newDate||new Date().toISOString().split('T')[0]
    });
    if(error){ui().toast('Failed to save: '+error.message,'error');return;}

    ui().closeModal();
    ui().toast('Allocation updated successfully','success');
    loadAllocations();
    } finally { restore(); }
  }

  async function recallAllocation(allocId,remainingQty,productId,warehouseId){
    ui().confirm(`Recall ${remainingQty} remaining units back to available stock?`,async()=>{
      const {data:sl}=await db().select('stock_levels',{eq:{product_id:productId,warehouse_id:warehouseId},maybeSingle:true,noCompanyFilter:true});
      if(sl){await db().update('stock_levels',sl.id,{reserved_by_allocations:Math.max(0,(sl.reserved_by_allocations||0)-remainingQty)});}
      await db().update('dropship_allocations',allocId,{remaining_qty:0});
      ui().toast(`${remainingQty} units returned to available stock`,'success');
      loadAllocations();
    },'Recall stock',false);
  }

  // ═══════════════════════════════════════════════════════════════
  // ORDERS
  // ═══════════════════════════════════════════════════════════════
  async function loadOrders(filterMode=''){
    const content=document.getElementById('ims-ds-content');
    if(!content)return;
    const actions=document.getElementById('ims-ds-actions');
    if(actions)actions.innerHTML=`<button class="ims__btn ims__btn--primary" onclick="window.IMS_DROPSHIP.showNewOrder()"><i class="ti ti-plus"></i> New dropship order</button>`;

    // filterMode 'self_arrange_pending' comes from dashboard card click
    const isSelfArrangePending = filterMode === 'self_arrange_pending';

    const search=document.getElementById('ims-ds-search')?.value?.toLowerCase()||'';
    const statusF=isSelfArrangePending ? '' : (document.getElementById('ims-ds-status')?.value||'');

    const _client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    let _query=_client.from('orders')
      .select('*,dropship_customers(business_name,delivery_method)')
      .eq('fulfillment_type','dropship')
      .order('order_number',{ascending:false});
    if(statusF) _query=_query.eq('status',statusF);
    const {data:orders}=await _query;
    _allDSOrders=orders||[];
    let items=_allDSOrders;

    // If coming from dashboard, pre-filter to self-arrange + not yet collected
    if(isSelfArrangePending){
      items=items.filter(o=>o.delivery_method==='self_arrange'&&!['collected','cancelled','delivered'].includes(o.status));
    } else if(search){
      items=items.filter(o=>o.order_number?.toLowerCase().includes(search)||o.end_customer_name?.toLowerCase().includes(search)||o.end_postcode?.toLowerCase().includes(search)||o.dropship_customers?.business_name?.toLowerCase().includes(search));
    }

    const filterBanner = isSelfArrangePending ? `
      <div class="ims__alert ims__alert--info" style="margin-bottom:12px">
        <i class="ti ti-filter"></i>
        <span>Showing <strong>self-arrange orders awaiting collection</strong> — courier has not yet collected.
        <button class="ims__btn ims__btn--ghost ims__btn--sm" style="margin-left:8px" onclick="window.IMS_DROPSHIP.loadOrders()">Show all orders</button></span>
      </div>` : '';

    content.innerHTML=`
      ${filterBanner}
      <div class="ims__filters" style="margin-bottom:14px">
        <div class="ims__search-wrap"><i class="ti ti-search"></i><input class="ims__input" type="text" id="ims-ds-search" placeholder="Order #, customer, postcode..." oninput="window.IMS_DROPSHIP.filterOrders()"></div>
        <select class="ims__select" id="ims-ds-status" onchange="window.IMS_DROPSHIP.filterOrders()">
          <option value="">All statuses</option>
          <option value="received">Received</option>
          <option value="picking">Picking</option>
          <option value="packed">Packed</option>
          <option value="dispatched">Dispatched</option>
          <option value="delivered">Delivered</option>
          <option value="collected">Collected</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      <div class="ims__table-wrap">
        <table class="ims__table">
          <thead><tr><th>Order #</th><th>Ref</th><th>Dropship customer</th><th>End customer</th><th>Postcode</th><th>Delivery</th><th>Value</th><th>Date</th><th>Status</th><th></th></tr></thead>
          <tbody>${items.map(o=>{
            const isSelfArrange=o.delivery_method==='self_arrange';
            const pendingCollection=isSelfArrange&&!['collected','cancelled','delivered'].includes(o.status);
            return`<tr${pendingCollection?' style="background:var(--ims-accent-bg)"':''}>
              <td><span class="ims__link" onclick="window.IMS_DROPSHIP.openOrder('${o.id}')">${util().sanitize(o.order_number)}</span></td>
              <td style="font-size:12px;color:var(--ims-text-muted)">${util().sanitize(o.external_ref||'—')}</td>
              <td>${util().sanitize(o.dropship_customers?.business_name||'—')}</td>
              <td>${util().sanitize(o.end_customer_name||'—')}</td>
              <td>${util().sanitize(o.end_postcode||'—')}</td>
              <td><span class="ims__pill ims__pill--${isSelfArrange?'warning':'info'}" style="font-size:10px">${isSelfArrange?'Self-arrange':'We deliver'}</span></td>
              <td>${util().formatCurrency(o.total_value)}</td>
              <td>${util().formatDate(o.created_at)}</td>
              <td>${util().pill(o.status)}</td>
              <td>
                <div class="ims__btn-group">
                  <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_DROPSHIP.openOrder('${o.id}')"><i class="ti ti-eye"></i></button>
                  ${window.SM.AUTH.isAdmin()?`<button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon" onclick="window.IMS_DROPSHIP.deleteOrder('${o.id}','${util().sanitize(o.order_number)}')" title="Delete order"><i class="ti ti-trash"></i></button>`:''}
                  ${pendingCollection?`
                    <button class="ims__btn ims__btn--success ims__btn--sm"
                      onclick="window.IMS_DROPSHIP.showCollectionModal('${o.id}','${util().sanitize(o.order_number)}','${util().sanitize(o.dropship_customers?.business_name||'')}')">
                      <i class="ti ti-truck-loading"></i> Confirm collection
                    </button>`:''}
                </div>
              </td>
            </tr>`;
          }).join('')||'<tr><td colspan="10" class="ims__table-empty">No dropship orders found</td></tr>'}
          </tbody>
        </table>
      </div>`;
  }

  let _dsOrderItems=[];
  let _dsActiveDeal=null; // set by checkForDSDeal() — active dropship deal for the selected customer

  async function showNewOrder(preCustomerId='',preCustomerName='',preDiscount=0,preDelivery='mdf_delivers'){
    _dsOrderItems=[];
    _dsActiveDeal=null;
    const {data:customers}=await db().select('dropship_customers',{eq:{is_active:true},order:{col:'business_name'}});
    const custOpts=(customers||[]).map(c=>`<option value="${c.id}" data-discount="${c.discount_pct||0}" data-delivery="${c.delivery_method}" ${c.id===preCustomerId?'selected':''}>${util().sanitize(c.business_name)}</option>`).join('');

    ui().openModal('New dropship order',`
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Dropship customer (retailer)</label>
          <select class="ims__select" id="ims-dso-customer" onchange="window.IMS_DROPSHIP.onDSCustomerChange()">
            <option value="">Select customer...</option>${custOpts}
          </select>
        </div>
        <div class="ims__form-group ims__form-group--full" style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
          <div><span class="ims__label">Discount</span><div id="ims-dso-discount-display" style="font-weight:600;color:var(--ims-success)">${preDiscount?preDiscount+'% off wholesale':'Select customer'}</div></div>
          <div><span class="ims__label">Their delivery method</span><div id="ims-dso-delivery-display"><span class="ims__pill ims__pill--${preDelivery==='mdf_delivers'?'info':'neutral'}">${preDelivery==='mdf_delivers'?'We deliver':'Self-arrange'}</span></div></div>
        </div>
        <div class="ims__form-group"><label class="ims__label">Retailer's order reference</label><input class="ims__input" type="text" id="ims-dso-extref" placeholder="Their order number"></div>
        <div class="ims__form-group"><label class="ims__label ims__label--req">Order date</label><input class="ims__input" type="date" id="ims-dso-orderdate" value="${new Date().toISOString().split('T')[0]}"></div>
      </div>
      <hr class="ims__divider">
      <strong style="font-size:13px">End customer details (who receives the delivery)</strong>
      <div class="ims__form-grid" style="margin-top:10px">
        <div class="ims__form-group"><label class="ims__label ims__label--req">End customer name</label><input class="ims__input" type="text" id="ims-dso-endname"></div>
        <div class="ims__form-group"><label class="ims__label ims__label--req">Postcode</label><input class="ims__input" type="text" id="ims-dso-endpostcode"></div>
        <div class="ims__form-group"><label class="ims__label">Phone</label><input class="ims__input" type="tel" id="ims-dso-endphone"></div>
        <div class="ims__form-group"><label class="ims__label">Email</label><input class="ims__input" type="email" id="ims-dso-endemail"></div>
        <div class="ims__form-group ims__form-group--full"><label class="ims__label ims__label--req">Delivery address</label><textarea class="ims__textarea" id="ims-dso-endaddress" rows="2"></textarea></div>
        <div class="ims__form-group ims__form-group--full"><label class="ims__label">Notes</label><input class="ims__input" type="text" id="ims-dso-notes" placeholder="Special delivery instructions"></div>
      </div>
      <hr class="ims__divider">
      <div id="ims-dso-deal-banner" class="ims__alert ims__alert--info" style="display:none;margin-bottom:10px"></div>
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px">
        <strong style="font-size:13px">Order items</strong>
        <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_DROPSHIP.addDSItem()"><i class="ti ti-plus"></i> Add item</button>
      </div>
      <div id="ims-dso-items"><div style="font-size:12px;color:var(--ims-text-muted)">No items added yet</div></div>
      <div style="display:flex;justify-content:flex-end;align-items:center;gap:12px;margin-top:10px">
        <span style="font-size:13px;color:var(--ims-text-sec)">Total:</span>
        <span style="font-size:20px;font-weight:700" id="ims-dso-total">${util().formatCurrency(0)}</span>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_DROPSHIP.saveDSOrder()"><i class="ti ti-check"></i> Create order</button>
    `,'xl');

    if(preCustomerId)onDSCustomerChange();
  }

  async function onDSCustomerChange(){
    const sel=document.getElementById('ims-dso-customer');
    if(!sel)return;
    const opt=sel.options[sel.selectedIndex];
    const discount=opt?.dataset?.discount||0;
    const delivery=opt?.dataset?.delivery||'mdf_delivers';
    const discEl=document.getElementById('ims-dso-discount-display');
    const delEl=document.getElementById('ims-dso-delivery-display');
    if(discEl)discEl.innerHTML=`<span style="font-weight:600;color:var(--ims-success)">${discount}% off wholesale</span>`;
    if(delEl)delEl.innerHTML=`<span class="ims__pill ims__pill--${delivery==='mdf_delivers'?'info':'neutral'}">${delivery==='mdf_delivers'?'We deliver':'Self-arrange'}</span>`;
    await checkForDSDeal(sel.value);
    renderDSItems();
  }

  // ─── DROPSHIP DEALS — ORDER-FLOW INTEGRATION ────────────────────
  // Looks up any active dropship deal targeting this customer (or all
  // dropship customers) and applies it to pricing. Mirrors the retail
  // deals flow in ims-orders.js/ims-deals.js, but simplified: dropship
  // has no platforms and no customer-type buckets — targeting is just
  // "all dropship customers" or specific ones — and the customer's
  // standing discount is always a flat percentage (discount_pct), not
  // the retail dual discount_type/discount_value pair.
  async function checkForDSDeal(customerId){
    _dsActiveDeal=null;
    if(!customerId){updateDSDealBanner();return;}
    const today=new Date().toISOString().split('T')[0];
    const {data:deals}=await db().select('dropship_deals',{
      select:'*, dropship_deal_customers(customer_id)',
      eq:{is_active:true},
      lte:{valid_from:today},
      gte:{valid_to:today}
    });

    const matches=(deals||[]).filter(d=>
      d.applies_to_all_customers||(d.dropship_deal_customers||[]).some(dc=>dc.customer_id===customerId)
    );

    if(matches.length){
      matches.sort((a,b)=>{
        const aSpecific=(a.dropship_deal_customers||[]).some(dc=>dc.customer_id===customerId)?1:0;
        const bSpecific=(b.dropship_deal_customers||[]).some(dc=>dc.customer_id===customerId)?1:0;
        if(aSpecific!==bSpecific) return bSpecific-aSpecific;
        return (b.discount_value||0)-(a.discount_value||0);
      });
      _dsActiveDeal=matches[0];
    }
    updateDSDealBanner();
  }

  function updateDSDealBanner(){
    const banner=document.getElementById('ims-dso-deal-banner');
    if(!banner)return;
    if(_dsActiveDeal){
      const discLabel=_dsActiveDeal.discount_type==='percentage'
        ?`${_dsActiveDeal.discount_value}% off`
        :`£${_dsActiveDeal.discount_value} off`;
      banner.style.display='';
      banner.innerHTML=`<i class="ti ti-tag"></i> Deal applied: <strong>${util().sanitize(_dsActiveDeal.name)}</strong> — ${discLabel} (${_dsActiveDeal.stacking_behaviour==='stack'?'stacks with':'replaces'} standing discount)`;
    } else {
      banner.style.display='none';
      banner.innerHTML='';
    }
  }

  // Convenience wrapper used throughout the order form — applies the
  // active deal if one exists (via calculateDSDealPrice, defined in the
  // DROPSHIP DEALS admin section below — function hoisting makes it
  // safe to call here regardless of declaration order), otherwise falls
  // back to the customer's plain flat discount_pct (existing behaviour).
  function _dsCalcPrice(wholesalePrice,discountPct){
    if(!wholesalePrice||wholesalePrice<=0) return 0;
    if(_dsActiveDeal){
      return calculateDSDealPrice(wholesalePrice,discountPct,_dsActiveDeal).finalPrice;
    }
    return parseFloat((wholesalePrice*(1-discountPct/100)).toFixed(2));
  }

  function addDSItem(){_dsOrderItems.push({productId:'',productName:'',sku:'',qty:1,wholesalePrice:0,unitPrice:0});renderDSItems();}

  async function renderDSItems(){
    const container=document.getElementById('ims-dso-items');
    if(!container)return;
    const sel=document.getElementById('ims-dso-customer');
    const discountPct=parseFloat(sel?.options[sel?.selectedIndex]?.dataset?.discount||0)||0;
    const priceLabel=_dsActiveDeal?'Their price (deal applied)':`Their price (${discountPct}% off)`;

    if(!_dsOrderItems.length){container.innerHTML='<div style="font-size:12px;color:var(--ims-text-muted)">No items added yet</div>';updateDSTotal();return;}
    container.innerHTML=`
      <div style="display:grid;grid-template-columns:2fr 70px 120px 120px auto;gap:8px;align-items:center;margin-bottom:4px;padding:0 2px">
        <div style="font-size:11px;font-weight:600;color:var(--ims-text-muted)">Product</div>
        <div style="font-size:11px;font-weight:600;color:var(--ims-text-muted)">Qty</div>
        <div style="font-size:11px;font-weight:600;color:var(--ims-text-muted)">Wholesale (£)</div>
        <div style="font-size:11px;font-weight:600;color:var(--ims-text-muted)">${priceLabel}</div>
        <div></div>
      </div>`+
    _dsOrderItems.map((item,i)=>{
      const theirPrice=_dsCalcPrice(item.wholesalePrice,discountPct);
      return`<div style="display:grid;grid-template-columns:2fr 70px 120px 120px auto;gap:8px;align-items:center;margin-bottom:8px">
        <div style="position:relative">
          <input class="ims__input" type="text" placeholder="Product name or SKU" value="${util().sanitize(item.productName)}" autocomplete="off"
            oninput="window.IMS_DROPSHIP.updateDSItem(${i},'productName',this.value);window.IMS_DROPSHIP.searchDSProduct(${i},this.value)">
          <div id="ims-ds-prod-drop-${i}" class="ims__ac-dropdown"></div>
        </div>
        <input class="ims__input" type="number" min="1" value="${item.qty}"
          oninput="window.IMS_DROPSHIP.updateDSItem(${i},'qty',+this.value||1)">
        <input class="ims__input" type="number" min="0" step="0.01"
          value="${item.wholesalePrice||''}" placeholder="0.00"
          oninput="window.IMS_DROPSHIP.updateDSItem(${i},'wholesalePrice',+this.value||0);window.IMS_DROPSHIP.updateDSTotal()"
          style="${!item.wholesalePrice?'border-color:var(--ims-warning)':''}">
        <div style="font-size:13px;font-weight:600;color:${_dsActiveDeal?'var(--ims-accent)':'var(--ims-success)'};padding:8px 10px;background:var(--ims-surface2);border-radius:var(--ims-radius);border:1px solid ${_dsActiveDeal?'var(--ims-accent)':'var(--ims-border)'}">
          ${theirPrice>0?util().formatCurrency(theirPrice):'—'}
          ${_dsActiveDeal&&theirPrice>0?'<div style="font-size:9px;font-weight:500;color:var(--ims-accent)"><i class="ti ti-tag"></i> Deal price</div>':''}
        </div>
        <button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon" onclick="window.IMS_DROPSHIP.removeDSItem(${i})"><i class="ti ti-trash"></i></button>
      </div>`;
    }).join('');
    updateDSTotal();
  }

  function updateDSItem(i,field,val){
    if(_dsOrderItems[i]){
      _dsOrderItems[i][field]=val;
      if(field==='qty'||field==='wholesalePrice') renderDSItems();
    }
  }
  function removeDSItem(i){_dsOrderItems.splice(i,1);renderDSItems();}

  async function searchDSProduct(index,query){
    if(!query||query.length<2){const d=document.getElementById(`ims-ds-prod-drop-${index}`);if(d)d.style.display='none';return;}
    // Search by name AND by SKU
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const [{data:byName},{data:bySku}]=await Promise.all([
      db().select('products',{ilike:{name:query},select:'id,name,sku',limit:5}),
      db().select('products',{ilike:{sku:query},select:'id,name,sku',limit:5})
    ]);
    const combined=[...(byName||[]),...(bySku||[])];
    const products=combined.filter((p,i,arr)=>arr.findIndex(x=>x.id===p.id)===i).slice(0,8);
    const drop=document.getElementById(`ims-ds-prod-drop-${index}`);
    if(!drop||!products?.length){if(drop)drop.style.display='none';return;}

    // Fetch all wholesale prices in one batch query
    const productIds=products.map(p=>p.id);
    const {data:prices}=await client
      .from('product_prices')
      .select('product_id,price')
      .in('product_id',productIds)
      .eq('price_type','wholesale')
      .is('platform_id',null);
    const priceMap={};
    (prices||[]).forEach(pp=>{priceMap[pp.product_id]=pp.price;});

    // Customer-specific flat overrides take priority over the shared
    // wholesale rate — per the confirmed pricing design, dropship
    // customers should never silently share the standard wholesale
    // rate for a product they have a set price for.
    const custSel=document.getElementById('ims-dso-customer');
    const custId=custSel?.value||null;
    const customMap={};
    if(custId){
      const {data:custPrices}=await db().select('dropship_customer_prices',{
        select:'product_id,unit_price', eq:{customer_id:custId}, in:{product_id:productIds}
      });
      (custPrices||[]).forEach(cp=>{customMap[cp.product_id]=cp.unit_price;});
    }

    drop.innerHTML=products.map(p=>{
      const wholesale=priceMap[p.id]??null;
      const hasCustom=customMap[p.id]!=null;
      const effectivePrice=hasCustom?customMap[p.id]:(wholesale||0);
      const priceLabel=hasCustom
        ?`<span style="color:var(--ims-accent);font-weight:600"> — Custom: ${util().formatCurrency(customMap[p.id])}</span>`
        :(wholesale!==null?' — Wholesale: '+util().formatCurrency(wholesale):'<span style="color:var(--ims-warning)"> — No price set</span>');
      return`<div class="ims__ac-item" onmousedown="window.IMS_DROPSHIP.selectDSProduct(${index},${JSON.stringify({id:p.id,name:p.name,sku:p.sku,wholesale:effectivePrice}).replace(/"/g,'&quot;')})">
        <strong>${util().sanitize(p.name)}</strong>
        <span style="font-size:11px;color:var(--ims-text-muted);margin-left:6px">${p.sku}${priceLabel}</span>
      </div>`;
    }).join('');
    drop.style.display='block';
  }

  function selectDSProduct(index,product){
    if(_dsOrderItems[index]){
      _dsOrderItems[index].productId=product.id;
      _dsOrderItems[index].productName=product.name;
      _dsOrderItems[index].sku=product.sku;
      _dsOrderItems[index].wholesalePrice=product.wholesale||0;
      const sel=document.getElementById('ims-dso-customer');
      const discountPct=parseFloat(sel?.options[sel?.selectedIndex]?.dataset?.discount||0)||0;
      _dsOrderItems[index].unitPrice=_dsCalcPrice(product.wholesale||0,discountPct);
    }
    renderDSItems();
    const drop=document.getElementById(`ims-ds-prod-drop-${index}`);if(drop)drop.style.display='none';
  }

  function updateDSTotal(){
    const sel=document.getElementById('ims-dso-customer');
    const discountPct=parseFloat(sel?.options[sel?.selectedIndex]?.dataset?.discount||0)||0;
    const total=_dsOrderItems.reduce((s,i)=>s+((i.qty||1)*_dsCalcPrice(i.wholesalePrice,discountPct)),0);
    const el=document.getElementById('ims-dso-total');if(el)el.textContent=util().formatCurrency(total);
  }

  async function saveDSOrder(){
    const g=id=>document.getElementById(id)?.value?.trim()||'';
    const customerId=g('ims-dso-customer');
    const endName=g('ims-dso-endname');
    const endPostcode=g('ims-dso-endpostcode').toUpperCase();
    const endAddress=g('ims-dso-endaddress');
    const sel=document.getElementById('ims-dso-customer');
    const opt=sel?.options[sel?.selectedIndex];
    const discountPct=parseFloat(opt?.dataset?.discount||0)||0;
    const deliveryMethod=opt?.dataset?.delivery||'mdf_delivers';

    if(!customerId){ui().toast('Please select a dropship customer','error');return;}
    if(!endName){ui().toast('End customer name is required','error');return;}
    if(!endPostcode){ui().toast('End customer postcode is required','error');return;}
    if(!endAddress){ui().toast('Delivery address is required','error');return;}
    if(!_dsOrderItems.length){ui().toast('Please add at least one item','error');return;}

    // Disable button and show saving state
    const saveBtn=document.querySelector('#ims-modal-footer .ims__btn--primary');
    if(saveBtn){saveBtn.disabled=true;saveBtn.innerHTML='<i class="ti ti-loader-2 ims__spin"></i> Creating order...';}

    // Show progress in modal body footer area
    const progressDiv=document.createElement('div');
    progressDiv.id='ims-dso-progress';
    progressDiv.style.cssText='margin-top:12px;padding:10px 14px;background:var(--ims-surface2);border-radius:var(--ims-radius);border:1px solid var(--ims-border)';
    progressDiv.innerHTML=`
      <div style="display:flex;justify-content:space-between;font-size:12px;color:var(--ims-text-muted);margin-bottom:5px">
        <span id="ims-dso-progress-label"><i class="ti ti-loader-2 ims__spin"></i> Generating order number...</span>
        <span id="ims-dso-progress-pct">0%</span>
      </div>
      <div style="background:var(--ims-border);border-radius:999px;height:6px;overflow:hidden">
        <div id="ims-dso-progress-bar" style="height:6px;width:0%;background:var(--ims-accent);border-radius:999px;transition:width .3s"></div>
      </div>`;
    const modalBody=document.getElementById('ims-modal-body');
    if(modalBody) modalBody.appendChild(progressDiv);

    const setProgress=(pct,label)=>{
      const bar=document.getElementById('ims-dso-progress-bar');
      const pctEl=document.getElementById('ims-dso-progress-pct');
      const labelEl=document.getElementById('ims-dso-progress-label');
      if(bar) bar.style.width=pct+'%';
      if(pctEl) pctEl.textContent=pct+'%';
      if(labelEl) labelEl.innerHTML=label;
    };

    const total=_dsOrderItems.reduce((s,i)=>s+((i.qty||1)*_dsCalcPrice(i.wholesalePrice,discountPct)),0);

    // Generate order number by finding the highest existing DSO number across ALL orders
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const {data:allDSO}=await client.from('orders')
      .select('order_number')
      .like('order_number','DSO-%')
      .order('order_number',{ascending:false})
      .limit(1);
    const lastNum=allDSO?.[0]?.order_number?parseInt(allDSO[0].order_number.replace('DSO-','')||'0'):0;
    const orderNumber=`DSO-${String(lastNum+1).padStart(5,'0')}`;

    setProgress(20,'Saving order record...');

    // Credit limit check
    const {data:custData}=await client.from('dropship_customers').select('credit_limit,business_name').eq('id',customerId).maybeSingle();
    if(custData?.credit_limit>0){
      const {data:openOrders}=await client.from('orders').select('total_value').eq('dropship_customer_id',customerId).eq('fulfillment_type','dropship').not('status','in','("collected","delivered","cancelled","refunded")');
      const outstanding=(openOrders||[]).reduce((s,o)=>s+(o.total_value||0),0);
      const remaining=custData.credit_limit-outstanding;
      if(total>remaining){
        const proceed=confirm(`⚠ Credit limit warning\n\nCustomer: ${custData.business_name}\nCredit limit: £${custData.credit_limit.toFixed(2)}\nOutstanding: £${outstanding.toFixed(2)}\nThis order: £${total.toFixed(2)}\nOver limit by: £${(total-remaining).toFixed(2)}\n\nDo you want to proceed anyway?`);
        if(!proceed){setProgress(0,'');return;}
      }
    }

    // Stock availability warning
    for(const item of _dsOrderItems){
      if(!item.productId)continue;
      const {data:sl}=await db().select('stock_levels',{
        eq:{product_id:item.productId},maybeSingle:true,noCompanyFilter:true
      });
      if(sl){
        const available=(sl.quantity||0)-(sl.reserved_qty||0);
        if((item.qty||1)>available){
          const proceed=confirm(`⚠ Stock warning\n\nProduct: ${item.productName}\nRequested: ${item.qty||1}\nAvailable: ${available}\n\nNot enough stock. Do you want to proceed anyway?`);
          if(!proceed){
            if(saveBtn){saveBtn.disabled=false;saveBtn.innerHTML='<i class="ti ti-check"></i> Create order';}
            if(progressDiv)progressDiv.remove();
            return;
          }
        }
      }
    }

    const orderDate=g('ims-dso-orderdate')||new Date().toISOString().split('T')[0];

    // Phase 2: insert into main orders table with fulfillment_type='dropship'
    // This means dropship orders appear in Order Management and Logistics
    const {data:order,error}=await db().insert('orders',{
      order_number:       orderNumber,
      fulfillment_type:   'dropship',
      order_type:         'wholesale',
      dropship_customer_id: customerId,
      customer_name:      opt?.text||'Dropship',
      status:             'received',
      total_value:        total,
      delivery_method:    deliveryMethod,
      end_customer_name:  endName,
      end_customer_phone: g('ims-dso-endphone')||null,
      end_customer_email: g('ims-dso-endemail')||null,
      end_address:        endAddress,
      end_postcode:       endPostcode,
      postcode:           endPostcode,
      address:            endAddress,
      notes:              g('ims-dso-notes')||null,
      external_ref:       g('ims-dso-extref')||null,
      created_at:         new Date(orderDate).toISOString(),
      dropship_deal_id:   _dsActiveDeal?.id||null,
      dropship_deal_name_snapshot: _dsActiveDeal?.name||null,
      dropship_deal_discount_applied: _dsActiveDeal?1:0,
      created_by:         window.SM.AUTH.user?.id
    });
    if(error){
      ui().toast('Failed: '+error.message,'error');
      if(saveBtn){saveBtn.disabled=false;saveBtn.innerHTML='<i class="ti ti-check"></i> Create order';}
      if(progressDiv) progressDiv.remove();
      return;
    }

    setProgress(50,`Saving ${_dsOrderItems.length} item${_dsOrderItems.length===1?'':'s'}...`);

    await Promise.all(_dsOrderItems.map(async item=>{
      const unitPrice=_dsCalcPrice(item.wholesalePrice,discountPct);
      // Use raw client — order_items has no company_id column, so db()'s
      // auto-injection would fail; explicit-field raw insert is required.
      const {error:itemErr}=await client.from('order_items').insert({
        order_id:     order.id,
        product_id:   item.productId||null,
        product_name: item.productName,
        sku:          item.sku||'',
        quantity:     item.qty||1,
        unit_price:   unitPrice,
        total_price:  (item.qty||1)*unitPrice,
        cost_price:   0
      });
      if(itemErr){console.warn('[DSO] order_items insert failed:',itemErr.message);}
      // Update allocation remaining qty
      if(item.productId){
        const {data:alloc}=await client.from('dropship_allocations').select('id,remaining_qty').eq('customer_id',customerId).eq('product_id',item.productId).gt('remaining_qty',0).order('created_at').limit(1).maybeSingle();
        if(alloc){await client.from('dropship_allocations').update({remaining_qty:Math.max(0,(alloc.remaining_qty||0)-(item.qty||1))}).eq('id',alloc.id);}
      }
    }));

    setProgress(80,'Setting up logistics...');

    if(deliveryMethod==='mdf_delivers'){
      await db().insert('logistics_jobs',{order_id:order.id,job_type:'delivery',status:'unassigned',notes:`Dropship order ${orderNumber} — End customer: ${endName} ${endPostcode}`});
    }

    setProgress(100,'Done!');
    await new Promise(r=>setTimeout(r,400));

    _dsOrderItems=[];
    _dsActiveDeal=null;
    ui().closeModal();
    ui().toast(`Dropship order ${orderNumber} created`+(deliveryMethod==='mdf_delivers'?' — added to logistics queue':''),'success',5000);
    loadOrders();
  }

  // ═══════════════════════════════════════════════════════════════
  // DROPSHIP DEALS
  // ═══════════════════════════════════════════════════════════════
  // Deliberately separate from ims-deals.js (retail/wholesale/trade
  // deals) per the split-architecture decision: dropship customers
  // live in a different table (dropship_customers) with a simpler
  // pricing model (a single flat discount_pct, not the retail
  // discount_type/discount_value pair) and simpler targeting (no
  // platforms, no customer-type buckets — just all dropship customers
  // or specific ones). Backed by dropship_deals / dropship_deal_customers.
  async function loadDSDeals(){
    const content=document.getElementById('ims-ds-content');
    if(!content)return;
    content.innerHTML=`<div class="ims__loading"><i class="ti ti-loader-2 ims__spin"></i></div>`;

    const actions=document.getElementById('ims-ds-actions');
    if(actions) actions.innerHTML=`<button class="ims__btn ims__btn--primary" onclick="window.IMS_DROPSHIP.showNewDSDeal()"><i class="ti ti-plus"></i> New dropship deal</button>`;

    const {data:deals,error}=await db().select('dropship_deals',{
      select:'*, dropship_deal_customers(customer_id)',
      order:{col:'valid_from',asc:false}
    });

    if(error){content.innerHTML=`<div class="ims__coming-soon"><i class="ti ti-tag"></i><h3>Failed to load deals</h3><p>${util().sanitize(error.message)}</p></div>`;return;}

    const today=new Date().toISOString().split('T')[0];
    const items=deals||[];
    const active=items.filter(d=>d.is_active&&d.valid_from<=today&&d.valid_to>=today);
    const upcoming=items.filter(d=>d.is_active&&d.valid_from>today);
    const expired=items.filter(d=>!d.is_active||d.valid_to<today);

    content.innerHTML=`
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:18px">
        <div class="ims__stat" style="padding:12px">
          <div class="ims__stat-label">Active deals</div>
          <div class="ims__stat-value ims__stat-value--success" style="font-size:24px">${active.length}</div>
        </div>
        <div class="ims__stat" style="padding:12px">
          <div class="ims__stat-label">Upcoming</div>
          <div class="ims__stat-value ims__stat-value--accent" style="font-size:24px">${upcoming.length}</div>
        </div>
        <div class="ims__stat" style="padding:12px">
          <div class="ims__stat-label">Expired</div>
          <div class="ims__stat-value" style="font-size:24px">${expired.length}</div>
        </div>
      </div>
      <div class="ims__card">
        <div class="ims__table-wrap">
          <table class="ims__table">
            <thead><tr>
              <th>Deal name</th><th>Discount</th><th>Stacking</th>
              <th>Targets</th><th>Valid from</th><th>Valid to</th>
              <th>Status</th><th></th>
            </tr></thead>
            <tbody>${items.map(d=>{
              const isActive=d.is_active&&d.valid_from<=today&&d.valid_to>=today;
              const isUpcoming=d.is_active&&d.valid_from>today;
              const statusPill=isActive
                ?'<span class="ims__pill ims__pill--success">Active</span>'
                :isUpcoming
                  ?'<span class="ims__pill ims__pill--accent">Upcoming</span>'
                  :'<span class="ims__pill ims__pill--neutral">Expired</span>';
              const discount=d.discount_type==='percentage'?`${d.discount_value}% off`:`£${d.discount_value} off`;
              const targets=d.applies_to_all_customers
                ?'All dropship customers'
                :`${(d.dropship_deal_customers||[]).length} specific customer${(d.dropship_deal_customers||[]).length===1?'':'s'}`;
              return`<tr>
                <td><strong>${util().sanitize(d.name)}</strong>${d.description?`<div style="font-size:11px;color:var(--ims-text-muted)">${util().sanitize(d.description)}</div>`:''}</td>
                <td><span style="font-weight:600;color:var(--ims-accent)">${discount}</span></td>
                <td><span class="ims__pill ims__pill--${d.stacking_behaviour==='stack'?'info':'neutral'}">${d.stacking_behaviour==='stack'?'Stacks':'Replaces'}</span></td>
                <td style="font-size:12px;max-width:200px">${targets}</td>
                <td style="font-size:12px">${util().formatDate(d.valid_from)}</td>
                <td style="font-size:12px;color:${d.valid_to<today?'var(--ims-danger)':''}">${util().formatDate(d.valid_to)}</td>
                <td>${statusPill}</td>
                <td><div class="ims__btn-group">
                  <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_DROPSHIP.editDSDeal('${d.id}')" title="Edit"><i class="ti ti-edit"></i></button>
                  <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_DROPSHIP.toggleDSDeal('${d.id}',${!d.is_active},'${util().sanitize(d.name)}')" title="${d.is_active?'Deactivate':'Activate'}">
                    <i class="ti ti-${d.is_active?'eye-off':'eye'}"></i>
                  </button>
                  ${window.SM.AUTH.isAdmin()?`<button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon" onclick="window.IMS_DROPSHIP.deleteDSDeal('${d.id}','${util().sanitize(d.name)}')" title="Delete"><i class="ti ti-trash"></i></button>`:''}
                </div></td>
              </tr>`;
            }).join('')||'<tr><td colspan="8" class="ims__table-empty">No dropship deals yet — create your first deal</td></tr>'}
            </tbody>
          </table>
        </div>
      </div>`;
  }

  async function showNewDSDeal(){ await _openDSDealForm(null); }
  async function editDSDeal(id){ await _openDSDealForm(id); }

  async function _openDSDealForm(existingId){
    let d=null, selCustomers=new Set();

    if(existingId){
      const {data:deal}=await db().select('dropship_deals',{select:'*, dropship_deal_customers(customer_id)',eq:{id:existingId},maybeSingle:true});
      d=deal;
      (d?.dropship_deal_customers||[]).forEach(dc=>selCustomers.add(dc.customer_id));
    }

    const {data:customers}=await db().select('dropship_customers',{eq:{is_active:true},order:{col:'business_name'}});
    const allCustomers=customers||[];

    const today=new Date().toISOString().split('T')[0];
    const nextMonth=new Date(Date.now()+30*86400000).toISOString().split('T')[0];

    ui().openModal(existingId?`Edit dropship deal: ${d?.name}`:'New dropship deal',`
      <!-- SECTION 1: Deal details -->
      <div style="font-size:13px;font-weight:600;color:var(--ims-text);margin-bottom:12px"><i class="ti ti-tag" style="color:var(--ims-accent)"></i> Deal details</div>
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Deal name</label>
          <input class="ims__input" type="text" id="ims-dsd-name" value="${util().sanitize(d?.name||'')}" placeholder="e.g. Q3 Volume Discount">
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label">Description</label>
          <input class="ims__input" type="text" id="ims-dsd-desc" value="${util().sanitize(d?.description||'')}" placeholder="Internal note about this deal">
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Discount type</label>
          <select class="ims__select" id="ims-dsd-dtype" onchange="window.IMS_DROPSHIP.onDSDealDiscountTypeChange()">
            <option value="percentage" ${(d?.discount_type||'percentage')==='percentage'?'selected':''}>Percentage off (%)</option>
            <option value="fixed_amount" ${d?.discount_type==='fixed_amount'?'selected':''}>Fixed amount off (£)</option>
          </select>
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Discount value</label>
          <input class="ims__input" type="number" step="0.01" min="0" id="ims-dsd-dvalue"
            value="${d?.discount_value||''}"
            placeholder="${(d?.discount_type||'percentage')==='percentage'?'e.g. 10 for 10% off':'e.g. 50 for £50 off'}"
            oninput="window.IMS_DROPSHIP.updateDSDealPreview()">
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Stacking behaviour</label>
          <select class="ims__select" id="ims-dsd-stack" onchange="window.IMS_DROPSHIP.updateDSDealPreview()">
            <option value="replace" ${(d?.stacking_behaviour||'replace')==='replace'?'selected':''}>Replace customer's standing discount</option>
            <option value="stack" ${d?.stacking_behaviour==='stack'?'selected':''}>Stack on top of standing discount</option>
          </select>
          <span class="ims__input-hint">
            Replace: deal discount used instead of the customer's standing % off wholesale.<br>
            Stack: deal discount applied after the standing discount.
          </span>
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Status</label>
          <select class="ims__select" id="ims-dsd-active">
            <option value="true" ${(d?.is_active!==false)?'selected':''}>Active</option>
            <option value="false" ${d?.is_active===false?'selected':''}>Inactive (draft)</option>
          </select>
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Valid from</label>
          <input class="ims__input" type="date" id="ims-dsd-from" value="${d?.valid_from||today}">
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Valid to</label>
          <input class="ims__input" type="date" id="ims-dsd-to" value="${d?.valid_to||nextMonth}">
        </div>
      </div>

      <hr class="ims__divider">

      <!-- SECTION 2: Customer targeting -->
      <div style="font-size:13px;font-weight:600;color:var(--ims-text);margin-bottom:12px"><i class="ti ti-users" style="color:var(--ims-accent)"></i> Customer targeting</div>
      <div style="margin-bottom:10px">
        <label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer;margin-bottom:8px">
          <input type="checkbox" id="ims-dsd-all-cust" ${d?.applies_to_all_customers?'checked':''}
            onchange="window.IMS_DROPSHIP.onDSDealAllCustomersChange()">
          <strong>Apply to ALL dropship customers</strong>
        </label>
      </div>
      <div id="ims-dsd-cust-detail" style="${d?.applies_to_all_customers?'opacity:0.4;pointer-events:none':''}">
        <div style="font-size:12px;font-weight:600;color:var(--ims-text-sec);margin-bottom:8px">Specific dropship customers:</div>
        <div style="max-height:220px;overflow-y:auto;border:1px solid var(--ims-border);border-radius:var(--ims-radius);padding:8px">
          ${allCustomers.length?allCustomers.map(c=>`
            <label style="display:flex;align-items:center;gap:8px;padding:5px 4px;cursor:pointer;font-size:13px;border-bottom:1px solid var(--ims-border)">
              <input type="checkbox" class="ims-dsd-cust-cb" value="${c.id}"
                ${selCustomers.has(c.id)?'checked':''}>
              <span>${util().sanitize(c.business_name)}</span>
            </label>`).join('')
          :'<div style="font-size:12px;color:var(--ims-text-muted);padding:8px">No dropship customers found</div>'}
        </div>
      </div>

      <!-- Price preview -->
      <hr class="ims__divider">
      <div style="font-size:13px;font-weight:600;color:var(--ims-text);margin-bottom:10px"><i class="ti ti-calculator" style="color:var(--ims-accent)"></i> Price preview</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;align-items:end">
        <div class="ims__form-group">
          <label class="ims__label">Example wholesale price (£)</label>
          <input class="ims__input" type="number" step="0.01" id="ims-dsd-preview-price" value="100" oninput="window.IMS_DROPSHIP.updateDSDealPreview()">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Customer standing discount (%)</label>
          <input class="ims__input" type="number" step="0.01" id="ims-dsd-preview-standing" value="0" oninput="window.IMS_DROPSHIP.updateDSDealPreview()">
        </div>
      </div>
      <div id="ims-dsd-preview" style="background:var(--ims-surface2);border-radius:var(--ims-radius);padding:12px;font-size:13px;margin-top:8px">
        Enter values above to see price calculation
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_DROPSHIP.saveDSDeal('${existingId||''}')"><i class="ti ti-check"></i> ${existingId?'Save changes':'Create deal'}</button>
    `,'xl');

    setTimeout(()=>updateDSDealPreview(),100);
  }

  function onDSDealDiscountTypeChange(){
    const type=document.getElementById('ims-dsd-dtype')?.value;
    const input=document.getElementById('ims-dsd-dvalue');
    if(input) input.placeholder=type==='percentage'?'e.g. 10 for 10% off':'e.g. 50 for £50 off';
    updateDSDealPreview();
  }

  function onDSDealAllCustomersChange(){
    const checked=document.getElementById('ims-dsd-all-cust')?.checked;
    const detail=document.getElementById('ims-dsd-cust-detail');
    if(detail){detail.style.opacity=checked?'0.4':'1';detail.style.pointerEvents=checked?'none':'';}
  }

  // Pure calculation — given a wholesale price, the customer's flat
  // standing discount %, and a deal record, returns the final price
  // plus a line-item breakdown (mirrors ims-deals.js's calculateDealPrice).
  function calculateDSDealPrice(wholesalePrice,customerDiscountPct,deal){
    let afterStanding=wholesalePrice;
    const breakdown=[];
    breakdown.push({label:'Wholesale price',amount:wholesalePrice,type:'base'});

    if(deal.stacking_behaviour==='stack'&&customerDiscountPct>0){
      const disc=wholesalePrice*customerDiscountPct/100;
      afterStanding=wholesalePrice-disc;
      breakdown.push({label:`Standing discount (${customerDiscountPct}%)`,amount:-disc,type:'standing'});
    }

    let finalPrice=afterStanding;
    if(deal.discount_type==='percentage'){
      const disc=afterStanding*deal.discount_value/100;
      finalPrice=afterStanding-disc;
      breakdown.push({label:`Deal: ${deal.name} (${deal.discount_value}% ${deal.stacking_behaviour==='stack'?'stacked':'replaces standing'})`,amount:-disc,type:'deal'});
    } else {
      finalPrice=afterStanding-deal.discount_value;
      breakdown.push({label:`Deal: ${deal.name} (£${deal.discount_value} off ${deal.stacking_behaviour==='stack'?'stacked':'replaces standing'})`,amount:-deal.discount_value,type:'deal'});
    }

    finalPrice=Math.max(0,finalPrice);
    breakdown.push({label:'Customer pays',amount:finalPrice,type:'final'});
    return{finalPrice:parseFloat(finalPrice.toFixed(2)),breakdown};
  }

  function updateDSDealPreview(){
    const preview=document.getElementById('ims-dsd-preview');
    if(!preview)return;
    const base=parseFloat(document.getElementById('ims-dsd-preview-price')?.value)||100;
    const standing=parseFloat(document.getElementById('ims-dsd-preview-standing')?.value)||0;
    const dtype=document.getElementById('ims-dsd-dtype')?.value||'percentage';
    const dvalue=parseFloat(document.getElementById('ims-dsd-dvalue')?.value)||0;
    const stack=document.getElementById('ims-dsd-stack')?.value||'replace';

    const result=calculateDSDealPrice(base,standing,{discount_type:dtype,discount_value:dvalue,stacking_behaviour:stack,name:'This deal'});
    const fmt=v=>`£${v.toFixed(2)}`;
    preview.innerHTML=result.breakdown.map(line=>{
      const isFinal=line.type==='final';
      const color=line.type==='deal'?'var(--ims-accent)':line.type==='standing'?'var(--ims-text-sec)':isFinal?'var(--ims-success)':'inherit';
      return `<div style="display:flex;justify-content:space-between;padding:${isFinal?'6px':'4px'} 0;${isFinal?'font-size:15px;font-weight:700':'border-bottom:1px solid var(--ims-border)'};color:${color}"><span>${line.label}</span><span>${fmt(line.amount)}</span></div>`;
    }).join('');
  }

  async function saveDSDeal(existingId){
    const restore=_withBtnSpin(event,'Saving...');
    try{
    const g=id=>document.getElementById(id)?.value?.trim()||'';
    const name=g('ims-dsd-name');
    if(!name){ui().toast('Deal name is required','error');return;}
    const dvalue=parseFloat(g('ims-dsd-dvalue'));
    if(!dvalue||dvalue<=0){ui().toast('Please enter a valid discount value','error');return;}
    const from=g('ims-dsd-from'), to=g('ims-dsd-to');
    if(!from||!to){ui().toast('Valid from and valid to dates are required','error');return;}
    if(to<from){ui().toast('Valid to must be after valid from','error');return;}

    const allCust=document.getElementById('ims-dsd-all-cust')?.checked||false;
    const specificCustomers=[...document.querySelectorAll('.ims-dsd-cust-cb:checked')].map(cb=>cb.value);
    if(!allCust&&!specificCustomers.length){ui().toast('Select "All dropship customers" or at least one specific customer','error');return;}

    const dealData={
      name,
      description:g('ims-dsd-desc')||null,
      discount_type:g('ims-dsd-dtype')||'percentage',
      discount_value:dvalue,
      stacking_behaviour:g('ims-dsd-stack')||'replace',
      applies_to_all_customers:allCust,
      valid_from:from,
      valid_to:to,
      is_active:document.getElementById('ims-dsd-active')?.value==='true',
      created_by:window.SM.AUTH.user?.id
    };

    // dropship_deal_customers has no company_id column — db().insert()
    // would try to inject one and fail, so a raw client is required for
    // that child table (matching how deal_customers is handled).
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);

    let dealId=existingId;
    if(existingId){
      const {error}=await db().update('dropship_deals',existingId,dealData);
      if(error){ui().toast('Failed: '+error.message,'error');return;}
      await client.from('dropship_deal_customers').delete().eq('deal_id',existingId);
    } else {
      const {data:newDeal,error}=await db().insert('dropship_deals',dealData);
      if(error){ui().toast('Failed: '+error.message,'error');return;}
      if(!newDeal?.id){ui().toast('Deal created but ID not returned','error');return;}
      dealId=newDeal.id;
    }

    if(!allCust&&specificCustomers.length){
      await Promise.all(specificCustomers.map(custId=>
        client.from('dropship_deal_customers').insert({deal_id:dealId,customer_id:custId})
      ));
    }

    ui().closeModal();
    ui().toast(`Dropship deal "${name}" ${existingId?'updated':'created'}`,'success');
    loadDSDeals();
    } finally { restore(); }
  }

  async function toggleDSDeal(id,newActive,name){
    await db().update('dropship_deals',id,{is_active:newActive});
    ui().toast(`"${name}" ${newActive?'activated':'deactivated'}`,'success');
    loadDSDeals();
  }

  async function deleteDSDeal(id,name){
    ui().confirm(
      `Delete dropship deal "${name}"? This cannot be undone. Orders that used this deal will keep their discount records.`,
      async()=>{
        const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
        await client.from('dropship_deal_customers').delete().eq('deal_id',id);
        const {error}=await db().remove('dropship_deals',id);
        if(error){ui().toast('Failed: '+error.message,'error');return;}
        ui().toast(`Dropship deal "${name}" deleted`,'success');
        loadDSDeals();
      },
      'Delete deal',
      true
    );
  }

  async function openOrder(id){
    const {data:o}=await db().select('orders',{
      select:'*,dropship_customers(business_name,discount_pct,delivery_method),order_items(*)',
      eq:{id},maybeSingle:true,noCompanyFilter:true
    });
    if(!o)return;
    const pendingCollection=o.delivery_method==='self_arrange'&&!['collected','cancelled','delivered'].includes(o.status);
    ui().openModal(`Dropship Order: ${o.order_number}`,`
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px">
        <div><span class="ims__label">Dropship customer</span><div style="font-weight:600">${util().sanitize(o.dropship_customers?.business_name||'—')}</div></div>
        <div><span class="ims__label">Retailer ref</span><div>${util().sanitize(o.external_ref||'—')}</div></div>
        <div><span class="ims__label">End customer</span><div style="font-weight:600">${util().sanitize(o.end_customer_name||'—')}</div></div>
        <div><span class="ims__label">End postcode</span><div>${util().sanitize(o.end_postcode||'—')}</div></div>
        <div style="grid-column:1/-1"><span class="ims__label">Delivery address</span><div>${util().sanitize(o.end_address||'—')}</div></div>
        <div><span class="ims__label">Delivery method</span><div><span class="ims__pill ims__pill--${o.delivery_method==='mdf_delivers'?'info':'neutral'}">${o.delivery_method==='mdf_delivers'?'MDF London delivers':'Customer self-arranges'}</span></div></div>
        <div><span class="ims__label">Status</span><div>${util().pill(o.status)}</div></div>
        <div><span class="ims__label">Order value</span><div style="font-weight:700;font-size:18px">${util().formatCurrency(o.total_value)}</div></div>
        ${o.collected_by?`
          <div><span class="ims__label">Collected by</span><div>${util().sanitize(o.collected_by)}</div></div>
          <div><span class="ims__label">Vehicle reg</span><div>${util().sanitize(o.collection_vehicle||'—')}</div></div>
          <div><span class="ims__label">Collected at</span><div>${util().formatDate(o.collected_at)}</div></div>`:''}
        ${o.notes?`<div style="grid-column:1/-1"><span class="ims__label">Notes</span><div>${util().sanitize(o.notes)}</div></div>`:''}
      </div>
      <hr class="ims__divider">
      <strong style="font-size:13px">Items</strong>
      <table class="ims__table" style="margin-top:8px">
        <thead><tr><th>Product</th><th>SKU</th><th>Qty</th><th>Unit price</th><th>Total</th></tr></thead>
        <tbody>${(o.order_items||[]).map(i=>`<tr>
          <td>${util().sanitize(i.product_name||'—')}</td>
          <td>${util().sanitize(i.sku||'—')}</td>
          <td>${i.quantity}</td>
          <td>${util().formatCurrency(i.unit_price)}</td>
          <td>${util().formatCurrency(i.total_price)}</td>
        </tr>`).join('')}
        <tr><td colspan="4" style="text-align:right;font-weight:600">Total</td><td style="font-weight:700">${util().formatCurrency(o.total_value)}</td></tr>
        </tbody>
      </table>
    `,`
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
        ${['collected','cancelled','delivered'].includes(o.status) ? `
          <div style="font-size:12px;color:var(--ims-text-muted);padding:6px 10px;background:var(--ims-surface2);border-radius:var(--ims-radius);border:1px solid var(--ims-border)">
            <i class="ti ti-lock" style="font-size:11px"></i>
            Order is <strong>${o.status}</strong> — status is final and cannot be changed
          </div>` : `
          <select class="ims__select" id="ims-dso-status-sel" style="font-size:12px;width:auto">
            <option value="">Update status...</option>
            <option>picking</option>
            <option>packed</option>
            ${o.delivery_method==='mdf_delivers'?'<option>dispatched</option><option>delivered</option>':''}
            <option>cancelled</option>
          </select>
          <button class="ims__btn ims__btn--primary" onclick="window.IMS_DROPSHIP.updateOrderStatus('${o.id}')">Update</button>`}
        ${pendingCollection?`
          <button class="ims__btn ims__btn--success"
            onclick="window.IMS_DROPSHIP.showCollectionModal('${o.id}','${o.order_number}','${util().sanitize(o.dropship_customers?.business_name||'')}')">
            <i class="ti ti-truck-loading"></i> Confirm collection
          </button>`:''}
        ${window.SM.AUTH.isAdmin()?`<button class="ims__btn ims__btn--danger" onclick="window.IMS_DROPSHIP.deleteOrder('${o.id}','${o.order_number}')"><i class="ti ti-trash"></i> Delete order</button>`:''}
        <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Close</button>
      </div>
    `,'xl');
  }

  async function updateOrderStatus(orderId){
    const sel=document.getElementById('ims-dso-status-sel');
    if(!sel?.value)return;
    // Prevent setting to collected via status dropdown — must use Confirm Collection button
    if(sel.value==='collected'){ui().toast('Use the Confirm Collection button to mark as collected','warning');return;}
    const updates={status:sel.value};
    if(sel.value==='dispatched')updates.dispatched_at=new Date().toISOString();
    if(sel.value==='delivered')updates.delivered_at=new Date().toISOString();
    await db().update('orders',orderId,updates);
    ui().closeModal();ui().toast('Order status updated','success');loadOrders();
  }

  // ─── COURIER COLLECTION CONFIRMATION ────────────────────────────
  // Called when courier arrives to collect a self-arrange order.
  // Requires columns on dropship_orders:
  //   collected_by text, collection_vehicle text, collected_at timestamptz
  // Run this migration if not already done:
  //   ALTER TABLE dropship_orders ADD COLUMN IF NOT EXISTS collected_by text;
  //   ALTER TABLE dropship_orders ADD COLUMN IF NOT EXISTS collection_vehicle text;
  //   ALTER TABLE dropship_orders ADD COLUMN IF NOT EXISTS collected_at timestamptz;
  //   ALTER TABLE dropship_orders ADD COLUMN IF NOT EXISTS collection_notes text;

  async function showCollectionModal(orderId, orderNumber, customerName){
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().slice(0,5);

    // Load order items so we can show what's being collected
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const {data:items} = await client
      .from('dropship_order_items')
      .select('product_name,sku,quantity')
      .eq('order_id', orderId);

    ui().openModal(`Confirm collection: ${orderNumber}`,`
      <div class="ims__alert ims__alert--warning" style="margin-bottom:16px">
        <i class="ti ti-truck-loading"></i>
        <span>A courier is collecting this order on behalf of <strong>${util().sanitize(customerName)}</strong>. Complete the details below before confirming — this cannot be undone.</span>
      </div>

      ${items?.length ? `
        <div style="margin-bottom:16px">
          <div style="font-size:12px;font-weight:600;color:var(--ims-text-muted);margin-bottom:6px">ITEMS BEING COLLECTED</div>
          <div style="border:1px solid var(--ims-border);border-radius:var(--ims-radius);overflow:hidden">
            ${items.map((item,i)=>`
              <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 12px;${i<items.length-1?'border-bottom:1px solid var(--ims-border)':''}">
                <div>
                  <div style="font-size:13px;font-weight:500">${util().sanitize(item.product_name||'—')}</div>
                  <div style="font-size:11px;color:var(--ims-text-muted)">${util().sanitize(item.sku||'')}</div>
                </div>
                <div style="font-weight:600;color:var(--ims-accent)">×${item.quantity}</div>
              </div>`).join('')}
          </div>
        </div>` : ''}

      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Collected by (driver/courier name)</label>
          <input class="ims__input" type="text" id="ims-col-by"
            placeholder="e.g. John Smith — Panther Logistics">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Vehicle registration</label>
          <input class="ims__input" type="text" id="ims-col-vehicle"
            placeholder="e.g. AB12 CDE" style="text-transform:uppercase">
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Collection date</label>
          <input class="ims__input" type="date" id="ims-col-date" value="${dateStr}">
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Collection time</label>
          <input class="ims__input" type="time" id="ims-col-time" value="${timeStr}">
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label">Notes</label>
          <input class="ims__input" type="text" id="ims-col-notes"
            placeholder="e.g. 2 boxes, driver checked contents, left copy of POD">
        </div>
      </div>

      <div class="ims__alert ims__alert--info" style="margin-top:4px">
        <i class="ti ti-info-circle"></i>
        <span>Stock will remain reserved — it has already left your warehouse. The allocation <strong>remaining qty</strong> will not change on collection (it reduces when orders are placed).</span>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--success"
        onclick="window.IMS_DROPSHIP.confirmCollection('${orderId}','${orderNumber}')">
        <i class="ti ti-circle-check"></i> Confirm collected
      </button>
    `,'lg');

    // Auto-focus driver name
    setTimeout(()=>document.getElementById('ims-col-by')?.focus(), 150);
  }

  async function confirmCollection(orderId, orderNumber){
    const collectedBy  = document.getElementById('ims-col-by')?.value?.trim();
    const vehicle      = document.getElementById('ims-col-vehicle')?.value?.trim().toUpperCase();
    const dateVal      = document.getElementById('ims-col-date')?.value;
    const timeVal      = document.getElementById('ims-col-time')?.value||'00:00';
    const notes        = document.getElementById('ims-col-notes')?.value?.trim();

    if(!collectedBy){ ui().toast('Please enter who collected the order','error'); return; }
    if(!dateVal){ ui().toast('Please enter the collection date','error'); return; }

    const collectedAt = new Date(`${dateVal}T${timeVal}:00`).toISOString();

    const {error} = await db().update('orders', orderId, {
      status:             'collected',
      collected_by:       collectedBy,
      collection_vehicle: vehicle||null,
      collected_at:       collectedAt,
      collection_notes:   notes||null
    });

    if(error){
      // If columns don't exist yet, fall back to just status update
      if(error.message?.includes('column')){
        await db().update('orders', orderId, {
          status:'collected',
          collected_at: collectedAt
        });
        ui().closeModal();
        ui().toast(`Order ${orderNumber} marked as collected. Run the SQL migration to store full collection details.`,'warning',7000);
      } else {
        ui().toast('Failed: '+error.message,'error');
        return;
      }
    } else {
      ui().closeModal();
      ui().toast(`Order ${orderNumber} confirmed collected by ${collectedBy}`,'success',5000);
    }

    const client2=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const {data:items}=await client2.from('order_items').select('product_id,quantity').eq('order_id',orderId);
    if(items?.length){
      await Promise.all(items.map(async item=>{
        if(!item.product_id)return;
        const {data:sl}=await db().select('stock_levels',{eq:{product_id:item.product_id},maybeSingle:true,noCompanyFilter:true});
        if(sl){
          await db().update('stock_levels',sl.id,{
            quantity:Math.max(0,(sl.quantity||0)-(item.quantity||0)),
            reserved_qty:Math.max(0,(sl.reserved_qty||0)-(item.quantity||0)),
            reserved_by_allocations:Math.max(0,(sl.reserved_by_allocations||0)-(item.quantity||0))
          });
        }
      }));
    }
    loadOrders();
    if(window.IMS_DASHBOARD?.load)window.IMS_DASHBOARD.load();
  }

  // ═══════════════════════════════════════════════════════════════
  // FORTNIGHTLY INVOICES
  // ═══════════════════════════════════════════════════════════════
  async function loadInvoices(){
    const content=document.getElementById('ims-ds-content');
    if(!content)return;
    const actions=document.getElementById('ims-ds-actions');
    if(actions)actions.innerHTML=`<button class="ims__btn ims__btn--primary" onclick="window.IMS_DROPSHIP.generateInvoices()"><i class="ti ti-file-invoice"></i> Generate fortnightly invoices</button>`;

    // Dropship invoices now live in the shared invoices table
    // (source_module='dropship'), not the retired dropship_invoices —
    // customer_name/address/postcode are stored directly on each
    // invoice row (snapshotted at generation time), so no join needed.
    const {data:invoices}=await db().select('invoices',{
      eq:{source_module:'dropship'},
      order:{col:'created_at',asc:false}
    });
    const items=invoices||[];
    content.innerHTML=`
      <div class="ims__table-wrap">
        <table class="ims__table">
          <thead><tr><th>Invoice #</th><th>Customer</th><th>Invoice date</th><th>Total</th><th>Due date</th><th>Status</th><th></th></tr></thead>
          <tbody>${items.map(inv=>`<tr>
            <td><strong>${util().sanitize(inv.invoice_number)}</strong></td>
            <td>${util().sanitize(inv.customer_name||'—')}</td>
            <td style="font-size:12px">${util().formatDate(inv.invoice_date)}</td>
            <td style="font-weight:600">${util().formatCurrency(inv.gross_amount)}</td>
            <td style="color:${inv.due_date&&new Date(inv.due_date)<new Date()&&inv.status!=='paid'?'var(--ims-danger)':''}">${inv.due_date?util().formatDate(inv.due_date):'—'}</td>
            <td>${util().pill(inv.status)}</td>
            <td><div class="ims__btn-group">
              ${inv.status==='draft'?`<button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_DROPSHIP.emailInvoice('${inv.id}')"><i class="ti ti-send"></i> Send</button>`:''}
              ${inv.status==='sent'?`<button class="ims__btn ims__btn--success ims__btn--sm" onclick="window.IMS_DROPSHIP.markPaid('${inv.id}')"><i class="ti ti-check"></i> Mark paid</button>`:''}
              <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_ACCOUNTS.openInvoice('${inv.id}')" title="View"><i class="ti ti-eye"></i></button>
              <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_ACCOUNTS.printInvoice('${inv.id}')" title="Print"><i class="ti ti-printer"></i></button>
            </div></td>
          </tr>`).join('')||'<tr><td colspan="7" class="ims__table-empty">No invoices yet — click Generate to create fortnightly invoices</td></tr>'}
          </tbody>
        </table>
      </div>`;
  }

  async function generateInvoices(){
    const {data:customers}=await db().select('dropship_customers',{eq:{is_active:true},order:{col:'business_name'}});
    if(!customers?.length){ui().toast('No active dropship customers','warning');return;}
    const custOpts='<option value="all">All active customers</option>'+
      customers.map(c=>`<option value="${c.id}">${util().sanitize(c.business_name)}</option>`).join('');

    // Build fortnightly periods going back 6 months — newest first
    // Each month = 2 periods: 1st-15th and 16th-last day of month
    const periods=[];
    const now=new Date();
    for(let monthOffset=0;monthOffset<6;monthOffset++){
      const refDate=new Date(now.getFullYear(),now.getMonth()-monthOffset,1);
      const y=refDate.getFullYear();
      const m=String(refDate.getMonth()+1).padStart(2,'0');
      const lastDay=new Date(y,refDate.getMonth()+1,0).getDate(); // true last day of month
      const monthLabel=refDate.toLocaleDateString('en-GB',{month:'long',year:'numeric'});

      // Second half: 16th to last day — add this first (most recent within month)
      const s2=`${y}-${m}-16`;
      const e2=`${y}-${m}-${String(lastDay).padStart(2,'0')}`;
      if(new Date(s2)<=now){
        periods.push({s:s2,e:e2,label:`16 — ${lastDay} ${monthLabel}`});
      }

      // First half: 1st to 15th
      const s1=`${y}-${m}-01`;
      const e1=`${y}-${m}-15`;
      if(new Date(s1)<=now){
        periods.push({s:s1,e:e1,label:`01 — 15 ${monthLabel}`});
      }
    }

    ui().openModal('Generate fortnightly invoices',`
      <div class="ims__alert ims__alert--info" style="margin-bottom:14px">
        <i class="ti ti-info-circle"></i>
        <span>Select one or more fortnightly periods and the customer. An invoice will be generated per period per customer covering all orders in that range.</span>
      </div>
      <div class="ims__form-group" style="margin-bottom:14px">
        <label class="ims__label">Customer</label>
        <select class="ims__select" id="ims-gi-customer">${custOpts}</select>
      </div>
      <label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer;margin-bottom:14px">
        <input type="checkbox" id="ims-gi-novat">
        Generate without VAT (zero-rated invoice)
      </label>
      <div class="ims__form-grid" style="margin-bottom:14px">
        <div class="ims__form-group">
          <label class="ims__label">Account ref (optional)</label>
          <input class="ims__input" type="text" id="ims-gi-accref" placeholder="e.g. HAPPY01">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Carriage net (£)</label>
          <input class="ims__input" type="number" step="0.01" min="0" id="ims-gi-carriage" value="0">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Total packages (optional)</label>
          <input class="ims__input" type="number" min="0" id="ims-gi-packages">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Incoterms (optional)</label>
          <input class="ims__input" type="text" id="ims-gi-incoterms" placeholder="e.g. DAP">
        </div>
      </div>
      <div class="ims__label" style="margin-bottom:8px">Select billing period(s)</div>
      <div style="border:1px solid var(--ims-border);border-radius:var(--ims-radius);overflow:hidden">
        ${periods.map((p,i)=>`
          <label style="display:flex;align-items:center;gap:10px;padding:10px 14px;cursor:pointer;
            border-bottom:${i<periods.length-1?'1px solid var(--ims-border)':'none'};
            background:var(--ims-surface)"
            onmouseenter="this.style.background='var(--ims-surface2)'"
            onmouseleave="this.style.background='var(--ims-surface)'">
            <input type="checkbox" value="${p.s}|${p.e}"
              style="width:16px;height:16px;accent-color:var(--ims-accent);flex-shrink:0">
            <span style="font-size:13px">${p.label}</span>
          </label>`).join('')}
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_DROPSHIP.runGenerateInvoices()">
        <i class="ti ti-file-invoice"></i> Generate invoices
      </button>
    `,'sm');
  }

  function setInvoicePeriod(){}  // no longer needed, kept for safety

  async function runGenerateInvoices(){
    const custFilter=document.getElementById('ims-gi-customer')?.value;
    const checked=[...document.querySelectorAll('#ims-modal-body input[type=checkbox]:checked')];
    if(!checked.length){ui().toast('Please select at least one period','error');return;}

    const {data:allCustomers}=await db().select('dropship_customers',{eq:{is_active:true}});
    const customers=custFilter==='all'?allCustomers:(allCustomers||[]).filter(c=>c.id===custFilter);
    if(!customers?.length){ui().toast('No customers found','warning');return;}

    const noVat=document.getElementById('ims-gi-novat')?.checked||false;
    const accountRef=document.getElementById('ims-gi-accref')?.value?.trim()||null;
    const carriageAmount=parseFloat(document.getElementById('ims-gi-carriage')?.value)||0;
    const totalPackages=document.getElementById('ims-gi-packages')?.value?parseInt(document.getElementById('ims-gi-packages').value):null;
    const incoterms=document.getElementById('ims-gi-incoterms')?.value?.trim()||null;

    ui().closeModal();
    let created=0,skipped=0;

    // Dropship invoices now live in the same shared invoices/invoice_lines
    // tables Accounts uses (source_module='dropship' distinguishes their
    // origin) rather than the old, separate dropship_invoices table —
    // one true invoicing system for the whole app, per the confirmed
    // migration/standardization plan.
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);

    for(const chk of checked){
      const [pStart,pEnd]=chk.value.split('|');
      for(const c of customers){
        // invoices has no customer_id column at all — Accounts stores
        // customer identity as plain snapshotted text fields
        // (customer_name/address/postcode/email/vat_number), never a
        // foreign key. Matching on name+date+source instead.
        const {data:existing}=await db().select('invoices',{
          eq:{source_module:'dropship',customer_name:c.business_name,invoice_date:pStart},maybeSingle:true
        });
        if(existing){skipped++;continue;}

        const {data:orders}=await db().select('orders',{
          select:'id,total_value',
          eq:{dropship_customer_id:c.id,fulfillment_type:'dropship'},
          gte:{created_at:pStart+'T00:00:00'},
          lte:{created_at:pEnd+'T23:59:59'}
        });
        if(!orders?.length){skipped++;continue;}

        const orderIds=orders.map(o=>o.id);
        const {data:orderItems}=await client.from('order_items')
          .select('order_id,product_id,product_name,sku,quantity,unit_price,total_price,products(hs_tariff_code,country_of_origin,unit_weight_kg)')
          .in('order_id',orderIds);

        const netTotal=(orderItems||[]).reduce((s,it)=>s+(it.total_price||0),0);
        const vatRate=noVat?0:(window.SM.AUTH.company?.vat_rate||20)/100;
        const vatAmt=noVat?0:parseFloat((netTotal*vatRate).toFixed(2));
        const grossTotal=parseFloat((netTotal+vatAmt+carriageAmount).toFixed(2));

        const {data:lastInv}=await db().select('invoices',{
          select:'invoice_number',eq:{source_module:'dropship'},order:{col:'created_at',asc:false},limit:1
        });
        const num=lastInv?.[0]?.invoice_number?parseInt(lastInv[0].invoice_number.split('-').pop()||0)+1:1;
        const invoiceNumber=`DSI-${String(num).padStart(5,'0')}`;

        const dueDate=new Date(pEnd);
        dueDate.setDate(dueDate.getDate()+14);

        const {data:inv,error}=await db().insert('invoices',{
          invoice_type:'sales',
          source_module:'dropship',
          customer_name:c.business_name,
          customer_address:c.address||null,
          customer_postcode:c.postcode||null,
          customer_email:c.email||null,
          customer_vat_number:c.vat_number||null,
          eori_no:c.eori_no||null,
          account_ref:accountRef||c.account_ref||null,
          invoice_date:pStart,
          due_date:dueDate.toISOString().split('T')[0],
          net_amount:parseFloat(netTotal.toFixed(2)),
          vat_rate:noVat?0:(window.SM.AUTH.company?.vat_rate||20),
          vat_amount:vatAmt,
          carriage_amount:carriageAmount,
          total_packages:totalPackages,
          incoterms:incoterms,
          gross_amount:grossTotal,
          currency:'GBP',
          status:'draft',
          invoice_number:invoiceNumber
        });

        if(error){
          console.error('[Dropship invoice generation] Insert failed for',c.business_name,pStart,'—',error.message);
          ui().toast(`Failed for ${c.business_name}: ${error.message}`,'error',8000);
          skipped++;
          continue;
        }

        if(!error&&inv){
          await Promise.all((orderItems||[]).map((it,idx)=>
            client.from('invoice_lines').insert({
              invoice_id:inv.id,
              description:it.product_name,
              product_code:it.sku,
              quantity:it.quantity,
              unit_price:it.unit_price,
              net_amount:it.total_price,
              vat_rate:noVat?0:(window.SM.AUTH.company?.vat_rate||20),
              vat_amount:noVat?0:parseFloat((it.total_price*vatRate).toFixed(2)),
              gross_amount:noVat?it.total_price:parseFloat((it.total_price*(1+vatRate)).toFixed(2)),
              hs_tariff_code:it.products?.hs_tariff_code||null,
              country_of_origin:it.products?.country_of_origin||null,
              unit_weight_kg:it.products?.unit_weight_kg||null,
              sort_order:idx
            })
          ));
          await Promise.all(orders.map(o=>client.from('orders').update({invoice_id:inv.id}).eq('id',o.id)));
          created++;
        }
      }
    }
    ui().toast(`${created} invoice(s) generated${skipped>0?`, ${skipped} skipped`:''}`,'success',5000);
    loadInvoices();
  }

  async function emailInvoice(id){
    const {data:inv}=await db().select('invoices',{
      eq:{id},maybeSingle:true,noCompanyFilter:true
    });
    if(!inv)return;
    // customer_email is snapshotted directly on the invoice at
    // generation time — no customer_id column exists to look back
    // through to dropship_customers.
    const company=window.SM.AUTH.company;
    const body=`Dear ${inv.customer_name||'Customer'},\n\nPlease find attached your invoice dated ${util().formatDate(inv.invoice_date)}.\n\nInvoice number: ${inv.invoice_number}\nTotal amount: ${util().formatCurrency(inv.gross_amount)} (incl. VAT ${util().formatCurrency(inv.vat_amount)})\nDue date: ${inv.due_date?util().formatDate(inv.due_date):'14 days'}\n\nPlease transfer payment to:\nBank: ${company?.bank_name||'—'}\nAccount: ${company?.bank_account||'—'}\nSort code: ${company?.bank_sort_code||'—'}\n\nKind regards,\n${company?.name||'MDF London Ltd'}\n${company?.email||''}`;

    ui().openModal(`Email invoice: ${inv.invoice_number}`,`
      <div class="ims__form-group" style="margin-bottom:10px">
        <label class="ims__label">To</label>
        <input class="ims__input" type="email" id="ims-inv-email-to" value="${util().sanitize(inv.customer_email||'')}">
      </div>
      <div class="ims__form-group">
        <label class="ims__label">Message</label>
        <textarea class="ims__textarea" id="ims-inv-email-body" rows="12">${body}</textarea>
      </div>
      <div class="ims__alert ims__alert--info" style="margin-top:8px"><i class="ti ti-info-circle"></i>
        <span>Copy this email and send from your mail client. SMTP sending will be available in a future update.</span>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_DROPSHIP.confirmEmailSent('${id}')"><i class="ti ti-check"></i> Mark as sent</button>
    `,'lg');
  }

  async function confirmEmailSent(id){
    await db().update('invoices',id,{status:'sent',sent_at:new Date().toISOString()});
    ui().closeModal();ui().toast('Invoice marked as sent','success');loadInvoices();
  }

  async function markPaid(id){
    await db().update('invoices',id,{status:'paid',paid_at:new Date().toISOString()});
    ui().toast('Invoice marked as paid','success');loadInvoices();
  }

  // ═══════════════════════════════════════════════════════════════
  // WEEKLY REPORT
  // ═══════════════════════════════════════════════════════════════
  async function loadReports(){
    const content=document.getElementById('ims-ds-content');
    if(!content)return;
    const actions=document.getElementById('ims-ds-actions');
    if(actions)actions.innerHTML='';
    const {data:customers}=await db().select('dropship_customers',{eq:{is_active:true},order:{col:'business_name'}});
    const custOpts=(customers||[]).map(c=>`<option value="${c.id}">${util().sanitize(c.business_name)}</option>`).join('');
    const weekAgo=new Date(Date.now()-7*86400000).toISOString().split('T')[0];
    const today=new Date().toISOString().split('T')[0];
    content.innerHTML=`
      <div class="ims__card" style="margin-bottom:14px">
        <div class="ims__form-grid">
          <div class="ims__form-group ims__form-group--full">
            <label class="ims__label ims__label--req">Select dropship customer</label>
            <select class="ims__select" id="ims-dsr-customer"><option value="">Choose customer...</option>${custOpts}</select>
          </div>
          <div class="ims__form-group"><label class="ims__label">Week from</label><input class="ims__input" type="date" id="ims-dsr-from" value="${weekAgo}"></div>
          <div class="ims__form-group"><label class="ims__label">Week to</label><input class="ims__input" type="date" id="ims-dsr-to" value="${today}"></div>
        </div>
        <div class="ims__btn-group" style="margin-top:10px">
          <button class="ims__btn ims__btn--primary" onclick="window.IMS_DROPSHIP.generateWeeklyReport()"><i class="ti ti-chart-bar"></i> Generate report</button>
        </div>
      </div>
      <div id="ims-dsr-output"></div>`;
  }

  async function generateWeeklyReport(){
    const customerId=document.getElementById('ims-dsr-customer')?.value;
    const from=document.getElementById('ims-dsr-from')?.value;
    const to=document.getElementById('ims-dsr-to')?.value;
    const output=document.getElementById('ims-dsr-output');
    if(!customerId){ui().toast('Please select a customer','error');return;}
    if(!output)return;
    output.innerHTML=`<div class="ims__loading"><i class="ti ti-loader-2 ims__spin"></i></div>`;

    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);

    const {data:customer}=await db().select('dropship_customers',{eq:{id:customerId},maybeSingle:true,noCompanyFilter:true});
    const {data:allocs}=await db().select('dropship_allocations',{
      select:'allocated_qty,remaining_qty,allocation_date,products(id,name,sku)',
      eq:{customer_id:customerId},noCompanyFilter:true
    });

    const {data:orders}=await client
      .from('orders')
      .select('id,order_number,external_ref,end_customer_name,end_customer_phone,end_address,end_postcode,status,total_value,delivery_method,delivered_at,collected_at,created_at')
      .eq('fulfillment_type','dropship')
      .eq('dropship_customer_id',customerId)
      .gte('created_at',from+'T00:00:00')
      .lte('created_at',to+'T23:59:59')
      .order('created_at',{ascending:true});

    const orderIds=(orders||[]).map(o=>o.id);
    let orderItems=[];
    if(orderIds.length){
      const {data:items}=await client
        .from('order_items')
        .select('order_id,product_name,sku,quantity')
        .in('order_id',orderIds);
      orderItems=items||[];
    }
    const itemsByOrder={};
    orderItems.forEach(i=>{
      if(!itemsByOrder[i.order_id])itemsByOrder[i.order_id]=[];
      itemsByOrder[i.order_id].push(i);
    });
    const enrichedOrders=(orders||[]).map(o=>({...o,items:itemsByOrder[o.id]||[]}));

    const {data:invoice}=await db().select('dropship_invoices',{
      select:'invoice_number,period_start,period_end,total_value,status,due_date',
      eq:{customer_id:customerId,status:'draft'},
      order:{col:'created_at',asc:false},
      limit:1,noCompanyFilter:true
    });

    const orderList=enrichedOrders;
    const allocationList=allocs||[];
    const totalOrders=orderList.length;
    const delivered=orderList.filter(o=>['delivered','collected'].includes(o.status)).length;
    const inProgress=orderList.filter(o=>!['delivered','collected','cancelled'].includes(o.status)).length;
    const totalValue=orderList.reduce((s,o)=>s+(o.total_value||0),0);

    const soldInPeriodIds=(orders||[]).filter(o=>['collected','delivered'].includes(o.status)).map(o=>o.id);
    let periodItems=[];
    if(soldInPeriodIds.length){
      const {data:pi}=await client.from('order_items').select('sku,product_name,quantity').in('order_id',soldInPeriodIds);
      periodItems=pi||[];
    }
    const soldBySkU={};
    periodItems.forEach(i=>{const sku=i.sku||i.product_name;soldBySkU[sku]=(soldBySkU[sku]||0)+(i.quantity||0);});

    const {data:priorOrders}=await client.from('orders')
      .select('id')
      .eq('fulfillment_type','dropship')
      .eq('dropship_customer_id',customerId)
      .in('status',['collected','delivered'])
      .lt('created_at',from+'T00:00:00');
    const priorOrderIds=(priorOrders||[]).map(o=>o.id);
    let priorItems=[];
    if(priorOrderIds.length){
      const {data:pi}=await client.from('order_items').select('sku,product_name,quantity').in('order_id',priorOrderIds);
      priorItems=pi||[];
    }
    const soldBeforePeriod={};
    priorItems.forEach(i=>{const sku=i.sku||i.product_name;soldBeforePeriod[sku]=(soldBeforePeriod[sku]||0)+(i.quantity||0);});

    const allocByProduct={};
    allocationList.forEach(a=>{
      const key=a.products?.sku||a.products?.id;
      if(!key)return;
      if(!allocByProduct[key])allocByProduct[key]={name:a.products?.name,sku:a.products?.sku,totalAllocated:0};
      allocByProduct[key].totalAllocated+=(a.allocated_qty||0);
    });
    Object.values(allocByProduct).forEach(p=>{
      p.opening=Math.max(0,p.totalAllocated-(soldBeforePeriod[p.sku]||0));
    });

    const allocTotalAlloc=Object.values(allocByProduct).reduce((s,p)=>s+(p.opening||0),0);
    const allocTotalSold=Object.values(allocByProduct).reduce((s,p)=>s+(soldBySkU[p.sku]||0),0);
    const allocTotalRemain=allocTotalAlloc-allocTotalSold;

    output.innerHTML=`
      <div class="ims__card" style="margin-bottom:14px">
        <div class="ims__card-header">
          <div class="ims__card-title"><i class="ti ti-chart-bar"></i>Weekly Dropship Report — ${util().sanitize(customer?.business_name||'')}</div>
          <div class="ims__btn-group">
            <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_DROPSHIP.printWeeklyReport('${customerId}','${from}','${to}')"><i class="ti ti-printer"></i> Print</button>
            <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_DROPSHIP.exportWeeklyReport()"><i class="ti ti-download"></i> Export CSV</button>
            <button class="ims__btn ims__btn--primary ims__btn--sm" onclick="window.IMS_DROPSHIP.emailWeeklyReport('${customerId}','${from}','${to}')"><i class="ti ti-send"></i> Email report</button>
          </div>
        </div>
        <div style="font-size:12px;color:var(--ims-text-muted);margin-bottom:12px">Period: ${util().formatDate(from)} — ${util().formatDate(to)}</div>

        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:16px">
          <div class="ims__stat" style="padding:10px"><div class="ims__stat-label">Total orders</div><div class="ims__stat-value" style="font-size:20px">${totalOrders}</div></div>
          <div class="ims__stat" style="padding:10px"><div class="ims__stat-label">Delivered</div><div class="ims__stat-value ims__stat-value--success" style="font-size:20px">${delivered}</div></div>
          <div class="ims__stat" style="padding:10px"><div class="ims__stat-label">In progress</div><div class="ims__stat-value" style="font-size:20px">${inProgress}</div></div>
          <div class="ims__stat" style="padding:10px"><div class="ims__stat-label">Week revenue</div><div class="ims__stat-value" style="font-size:16px">${util().formatCurrency(totalValue)}</div></div>
        </div>

        <strong style="font-size:13px;display:block;margin-bottom:8px">Orders this week (${totalOrders})</strong>
        <table class="ims__table" style="margin-bottom:16px">
          <thead><tr><th>Order #</th><th>Ref</th><th>End customer</th><th>Postcode</th><th>Product</th><th>Qty</th><th>Status</th><th>Collected</th></tr></thead>
          <tbody>${orderList.map(o=>`<tr>
            <td>${util().sanitize(o.order_number)}</td>
            <td>${util().sanitize(o.external_ref||'—')}</td>
            <td>${util().sanitize(o.end_customer_name||'—')}</td>
            <td>${util().sanitize(o.end_postcode||'—')}</td>
            <td style="font-size:11px">${(o.items||[]).map(i=>util().sanitize(i.product_name||'—')).join(', ')||'—'}</td>
            <td style="text-align:center">${(o.items||[]).reduce((s,i)=>s+(i.quantity||0),0)||1}</td>
            <td>${util().pill(o.status)}</td>
            <td>${o.collected_at?util().formatDate(o.collected_at):o.delivered_at?util().formatDate(o.delivered_at):'—'}</td>
          </tr>`).join('')||'<tr><td colspan="9" class="ims__table-empty">No orders in this period</td></tr>'}
          </tbody>
        </table>

        <strong style="font-size:13px;display:block;margin-bottom:8px">Stock allocation summary</strong>
        <table class="ims__table" style="margin-bottom:16px">
          <thead><tr><th>Product</th><th>SKU</th><th>Total allocated</th><th>Sold/delivered</th><th>Remaining</th></tr></thead>
          <tbody>${Object.values(allocByProduct).map(p=>`<tr>
            <td>${util().sanitize(p.name||'—')}</td>
            <td>${util().sanitize(p.sku||'—')}</td>
            <td>${p.opening}</td>
            <td>${soldBySkU[p.sku]||0}</td>
            <td style="font-weight:600;color:${Math.max(0,p.opening-(soldBySkU[p.sku]||0))>0?'var(--ims-success)':'var(--ims-text-muted)'}">${Math.max(0,p.opening-(soldBySkU[p.sku]||0))}</td>
          </tr>`).join('')||'<tr><td colspan="5" class="ims__table-empty">No stock allocated</td></tr>'}
          <tr style="font-weight:600;border-top:2px solid var(--ims-border);background:var(--ims-surface2)">
            <td>Totals</td>
            <td></td>
            <td>${allocTotalAlloc}</td>
            <td>${allocTotalSold}</td>
            <td style="color:${allocTotalRemain>0?'var(--ims-success)':'var(--ims-text-muted)'}">${allocTotalRemain}</td>
          </tr>
          </tbody>
        </table>

        ${invoice && invoice.invoice_number ? `
          <strong style="font-size:13px;display:block;margin-bottom:8px">Current fortnightly invoice</strong>
          <div class="ims__alert ims__alert--${invoice.status==='paid'?'success':invoice.status==='sent'?'info':'warning'}">
            <i class="ti ti-file-invoice"></i>
            <span><strong>${invoice.invoice_number}</strong> | Period: ${util().formatDate(invoice.period_start)} — ${util().formatDate(invoice.period_end)} | Total: <strong>${util().formatCurrency(invoice.total_value)}</strong> | Due: ${invoice.due_date?util().formatDate(invoice.due_date):'—'} | Status: ${invoice.status}</span>
          </div>` : '<div class="ims__alert ims__alert--info"><i class="ti ti-info-circle"></i><span>No current fortnightly invoice — click Invoices tab to generate.</span></div>'}
      </div>`;

    window._dsReportData={customer,orders:orderList,allocs:allocByProduct,soldBySkU,from,to};
  }

  async function emailWeeklyReport(customerId,from,to){
    const {data:customer}=await db().select('dropship_customers',{eq:{id:customerId},maybeSingle:true,noCompanyFilter:true});
    const company=window.SM.AUTH.company;
    const body=`Dear ${util().sanitize(customer?.business_name||'Customer')},\n\nPlease find below your summary dropship report for the period ${util().formatDate(from)} to ${util().formatDate(to)}.\n\nFor full details please log into the portal or contact us directly.\n\nKind regards,\n${company?.name||'MDF London Ltd'}\n${company?.email||''}`;

    ui().openModal('Email summary report',`
      <div class="ims__form-group" style="margin-bottom:10px">
        <label class="ims__label">To</label>
        <input class="ims__input" type="email" id="ims-wkr-email-to" value="${util().sanitize(customer?.email||'')}">
      </div>
      <div class="ims__form-group">
        <label class="ims__label">Message</label>
        <textarea class="ims__textarea" id="ims-wkr-email-body" rows="10">${body}</textarea>
      </div>
      <div class="ims__alert ims__alert--info" style="margin-top:8px"><i class="ti ti-info-circle"></i>
        <span>Copy this email and send from your mail client, attaching the printed report PDF. Full SMTP sending coming in next update.</span>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.SM.UI.closeModal();window.IMS_DROPSHIP.printWeeklyReport('${customerId}','${from}','${to}')"><i class="ti ti-printer"></i> Close & print report</button>
    `,'lg');
  }

  async function printWeeklyReport(customerId,from,to){
    const report=window._dsReportData;
    if(!report){ui().toast('Generate the report first','warning');return;}
    const {customer,orders,allocs,soldBySkU}=report;
    const now=new Date().toLocaleDateString('en-GB');

    const orderRows=orders.map(o=>[
      util().sanitize(o.order_number||'—'),
      util().sanitize(o.external_ref||'—'),
      util().sanitize(o.end_customer_name||'—'),
      util().sanitize(o.end_postcode||'—'),
      util().sanitize((o.items||[]).map(i=>i.product_name||'—').join(', ')||'—'),
      String((o.items||[]).reduce((s,i)=>s+(i.quantity||0),0)||1),
      util().sanitize(o.status||'—'),
      o.collected_at?util().formatDate(o.collected_at):o.delivered_at?util().formatDate(o.delivered_at):'—'
    ]);
    const orderTotalsRow=[
      '<td>Report total</td>','<td></td>','<td></td>','<td></td>','<td></td>',
      `<td class="r">${orders.length}</td>`,'<td></td>','<td></td>'
    ];
    const orderTableHtml=window.PRINT.table({
      colWidths:['10%','10%','20%','10%','25%','5%','10%','10%'],
      columns:[
        {label:'Order #'},{label:'Ref'},{label:'End customer'},{label:'Postcode'},
        {label:'Product'},{label:'Qty',align:'r'},{label:'Status'},{label:'Collected'}
      ],
      rows:orderRows,
      totalsRow:orderTotalsRow
    });

    const allocList=Object.values(allocs);
    const allocRows=allocList.map(p=>{
      const sold=soldBySkU?.[p.sku]||0;
      const remaining=Math.max(0,p.opening-sold);
      return [
        util().sanitize(p.name||'—'),
        util().sanitize(p.sku||'—'),
        String(p.opening),
        String(sold),
        String(remaining)
      ];
    });
    const allocTotalAlloc=allocList.reduce((s,p)=>s+(p.opening||0),0);
    const allocTotalSold=allocList.reduce((s,p)=>s+(soldBySkU?.[p.sku]||0),0);
    const allocTotalRemain=allocTotalAlloc-allocTotalSold;
    const allocTotalsRow=[
      '<td>Totals</td>','<td></td>',
      `<td class="r">${allocTotalAlloc}</td>`,
      `<td class="r">${allocTotalSold}</td>`,
      `<td class="r">${allocTotalRemain}</td>`
    ];
    const allocTableHtml=window.PRINT.table({
      colWidths:['35%','25%','13%','13%','14%'],
      columns:[
        {label:'Product'},{label:'SKU'},{label:'Allocated',align:'r'},
        {label:'Sold',align:'r'},{label:'Remaining',align:'r'}
      ],
      rows:allocRows,
      totalsRow:allocTotalsRow
    });

    const headerHtml=window.PRINT.header({
      title:'Dropship Activity Report',
      docRef:`Period: ${util().formatDate(from)} to ${util().formatDate(to)}`,
      rightLabel:'',
      rightName:customer?.business_name||'—',
      rightLines:[
        util().sanitize(customer?.address||''),
        util().sanitize(customer?.postcode||''),
        util().sanitize(customer?.email||''),
        `Date: ${now}`
      ]
    });

    const page=`
      ${headerHtml}
      <div class="st">Orders — ${util().sanitize(util().formatDate(from))} to ${util().sanitize(util().formatDate(to))}</div>
      <hr class="rule">
      ${orderTableHtml}
      <hr class="rule-dbl">
      <div class="st" style="margin-top:12px">Stock Allocation Summary</div>
      <hr class="rule">
      ${allocTableHtml}
      <hr class="rule-dbl">
      ${window.PRINT.pageFooter(['StockMaestro © 2026 ABSA Digital',`Dropship Report — ${customer?.business_name||''} — ${from} to ${to}`],1,1)}
    `;

    window.PRINT.renderDocument([page],`Dropship Report — ${customer?.business_name} — ${from} to ${to}`);
  }

  function exportWeeklyReport(){
    const report=window._dsReportData;
    if(!report){ui().toast('Generate the report first','warning');return;}
    const {customer,orders,allocs,from,to}=report;
    const headers=['Order #','End Customer','Address','Postcode','Phone','Delivery Method','Value','Status','Delivered Date'];
    const rows=(orders||[]).map(o=>[o.order_number,o.end_customer_name,o.end_address,o.end_postcode,o.end_customer_phone||'',o.delivery_method==='mdf_delivers'?'MDF Delivers':'Self-arrange',o.total_value,o.status,o.delivered_at?util().formatDate(o.delivered_at):o.collected_at?util().formatDate(o.collected_at):'']);
    util().exportCSV(headers,rows,`dropship_report_${customer?.business_name?.replace(/\s/g,'_')}_${from}_to_${to}`);
  }

  let _allDSOrders=[];

  function renderOrderRows(items){
    const tbody=document.querySelector('#ims-ds-content tbody');
    if(!tbody)return;
    tbody.innerHTML=items.map(o=>{
      const isSelfArrange=o.delivery_method==='self_arrange';
      const pendingCollection=isSelfArrange&&!['collected','cancelled','delivered'].includes(o.status);
      return`<tr${pendingCollection?' style="background:var(--ims-accent-bg)"':''}>
        <td><span class="ims__link" onclick="window.IMS_DROPSHIP.openOrder('${o.id}')">${util().sanitize(o.order_number)}</span></td>
        <td style="font-size:12px;color:var(--ims-text-muted)">${util().sanitize(o.external_ref||'—')}</td>
        <td>${util().sanitize(o.dropship_customers?.business_name||'—')}</td>
        <td>${util().sanitize(o.end_customer_name||'—')}</td>
        <td>${util().sanitize(o.end_postcode||'—')}</td>
        <td><span class="ims__pill ims__pill--${isSelfArrange?'warning':'info'}" style="font-size:10px">${isSelfArrange?'Self-arrange':'We deliver'}</span></td>
        <td>${util().formatCurrency(o.total_value)}</td>
        <td>${util().formatDate(o.created_at)}</td>
        <td>${util().pill(o.status)}</td>
        <td><div class="ims__btn-group">
          <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_DROPSHIP.openOrder('${o.id}')"><i class="ti ti-eye"></i></button>
          ${pendingCollection?`<button class="ims__btn ims__btn--success ims__btn--sm" onclick="window.IMS_DROPSHIP.showCollectionModal('${o.id}','${util().sanitize(o.order_number)}','${util().sanitize(o.dropship_customers?.business_name||'')}')"><i class="ti ti-truck-loading"></i> Confirm collection</button>`:''}
        </div></td>
      </tr>`;
    }).join('')||'<tr><td colspan="10" class="ims__table-empty">No dropship orders found</td></tr>';
  }

  function filterOrders(){
    const search=document.getElementById('ims-ds-search')?.value?.toLowerCase()||'';
    const statusF=document.getElementById('ims-ds-status')?.value||'';
    let items=_allDSOrders;
    if(statusF)items=items.filter(o=>o.status===statusF);
    if(search)items=items.filter(o=>
      o.order_number?.toLowerCase().includes(search)||
      o.end_customer_name?.toLowerCase().includes(search)||
      o.end_postcode?.toLowerCase().includes(search)||
      o.dropship_customers?.business_name?.toLowerCase().includes(search)
    );
    renderOrderRows(items);
  }

  // ─── BINDINGS ────────────────────────────────────────────────────
  window.addEventListener('ims:panel',e=>{
    if(e.detail.panel==='dropship'){initTabs();loadCustomers();}
  });

  async function deleteOrder(orderId, orderNumber) {
    if (!window.SM.AUTH.isAdmin()) { ui().toast('Admin access required', 'error'); return; }

    ui().openModal('Delete order', `
      <div class="ims__alert ims__alert--danger" style="margin-bottom:16px">
        <i class="ti ti-alert-triangle"></i>
        <span>You are about to permanently delete order <strong>${util().sanitize(orderNumber)}</strong>. This cannot be undone.</span>
      </div>
      <div style="font-size:13px;color:var(--ims-text-muted)">
        This will permanently delete the order and:<br><br>
        — Restore stock reservations and quantities<br>
        — Reverse any financial transactions<br>
        — Remove from any manifests<br>
        — Delete activity logs and logistics jobs
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--danger" onclick="window.IMS_DROPSHIP.confirmDeleteOrder('${orderId}','${util().sanitize(orderNumber)}')">
        <i class="ti ti-trash"></i> Yes, delete order
      </button>
    `, 'sm');
  }

  async function confirmDeleteOrder(orderId, orderNumber) {
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);

    const { data: order } = await db().select('orders', {
      select: 'status, dropship_customer_id, invoice_id',
      eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true
    });

    const isDelivered = ['collected', 'delivered'].includes(order?.status);

    const { data: items } = await client.from('order_items')
      .select('product_id, quantity')
      .eq('order_id', orderId);

    ui().closeModal();
    ui().toast('Deleting order — please wait...', 'info', 5000);

    // 1 — Restore stock levels
    if (items?.length) {
      await Promise.all(items.map(async item => {
        if (!item.product_id) return;
        const { data: sl } = await db().select('stock_levels', {
          eq: { product_id: item.product_id }, maybeSingle: true, noCompanyFilter: true
        });
        if (sl) {
          const updates = { reserved_qty: Math.max(0, (sl.reserved_qty || 0) - (item.quantity || 0)) };
          if (isDelivered) updates.quantity = (sl.quantity || 0) + (item.quantity || 0);
          await db().update('stock_levels', sl.id, updates);
        }
      }));
    }

    // 2 — Restore allocation remaining qty
    if (items?.length && order?.dropship_customer_id) {
      await Promise.all(items.map(async item => {
        if (!item.product_id) return;
        const { data: alloc } = await client.from('dropship_allocations')
          .select('id, remaining_qty')
          .eq('customer_id', order.dropship_customer_id)
          .eq('product_id', item.product_id)
          .order('created_at').limit(1).maybeSingle();
        if (alloc) {
          await client.from('dropship_allocations').update({
            remaining_qty: (alloc.remaining_qty || 0) + (item.quantity || 0)
          }).eq('id', alloc.id);
        }
      }));
    }

    // 3 — Delete transactions
    await client.from('transactions').delete().eq('related_id', orderId);
    await client.from('transactions').delete().eq('reference', orderNumber);

    // 4 — Delete AR entry
    await client.from('accounts_receivable').delete().eq('order_id', orderId);

    // 5 — Unlink from invoice and recalculate total
    if (order?.invoice_id) {
      await client.from('orders').update({ invoice_id: null }).eq('id', orderId);
      const { data: remainingOrders } = await client.from('orders').select('total_value').eq('invoice_id', order.invoice_id);
      const newNet = (remainingOrders || []).reduce((s, o) => s + (o.total_value || 0), 0);
      const vatRate = (window.SM.AUTH.company?.vat_rate || 20) / 100;
      const newVat = parseFloat((newNet * vatRate).toFixed(2));
      await client.from('dropship_invoices').update({
        total_value: parseFloat((newNet + newVat).toFixed(2)),
        vat_amount: newVat
      }).eq('id', order.invoice_id);
    }

    // 6 — Remove from manifest
    await client.from('manifest_items').delete().eq('order_id', orderId);

    // 7 — Delete logistics job
    await client.from('logistics_jobs').delete().eq('order_id', orderId);

    // 8 — Delete activity log
    await client.from('order_activity_log').delete().eq('order_id', orderId);

    // 9 — Delete COD transactions
    await client.from('cod_transactions').delete().eq('order_id', orderId);

    // 10 — Delete order items
    await client.from('order_items').delete().eq('order_id', orderId);

    // 11 — Delete the order
    await db().remove('orders', orderId);

    ui().toast(`Order ${orderNumber} deleted — all records reversed`, 'success', 5000);
    loadOrders();
  }

  window.IMS_DROPSHIP={
    loadCustomers,openCustomer,showCustomerForm,saveCustomer,deleteCustomer,confirmDeleteCustomer,
    showCustomerPricing,saveCustomPrice,deleteCustomPrice,
    showAllocateForm,renderAllocSearch,allocToggleProduct,allocConfirmSelection,
    allocUpdateAvail,allocValidateQty,allocRemoveRow,saveAllocationBulk,
    checkAvailableStock,
    loadAllocations,editAllocation,editAllocUpdateAvail,saveEditAllocation,recallAllocation,
    loadOrders,filterOrders,renderOrderRows,showNewOrder,deleteOrder,confirmDeleteOrder,onDSCustomerChange,addDSItem,
    updateDSItem,removeDSItem,searchDSProduct,selectDSProduct,
    saveDSOrder,openOrder,updateOrderStatus,showCollectionModal,confirmCollection,
    loadInvoices,generateInvoices,setInvoicePeriod,runGenerateInvoices,emailInvoice,
    confirmEmailSent,markPaid,
    loadReports,generateWeeklyReport,emailWeeklyReport,
    printWeeklyReport,exportWeeklyReport,
    loadDSDeals,showNewDSDeal,editDSDeal,saveDSDeal,toggleDSDeal,deleteDSDeal,
    onDSDealDiscountTypeChange,onDSDealAllCustomersChange,updateDSDealPreview
  };
})();
